import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { DomSanitizer } from '@angular/platform-browser';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonIcon } from '@ionic/angular/standalone';
import * as domtoimage from 'dom-to-image';
import saveAs from 'file-saver';
import ObjectID from 'bson-objectid';
import { firstValueFrom, Subject, Subscription, combineLatest } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import {
  CopilotExport,
  generateConversationTitle,
  IChatConversation,
  IChatConversationScope,
  IPromptAuditEntry
} from 'src/app/model/chat-conversation';
import { ensureSelectedModelOption, getFirstVisionModelId, getVisionModelOptions, IModelCatalogs, IProviderModelCatalog, IProviderModelOption } from 'src/app/model/configuration/model-catalog';
import {
  ActionProposal,
  AIProvider,
  AnnotateFindingActionProposal,
  ApplyProjectToSetupPrefillActionProposal,
  AutoAssignDevicesActionProposal,
  ChatLocalToolGroupId,
  ChatLocalToolId,
  ChatAttachmentIndicator,
  ChatCsvAttachment,
  CreateExperimentFromChatActionProposal,
  CreateOrAdjustWizardConfigActionProposal,
  DuplicateExperimentWizardActionProposal,
  ChatHiddenContext,
  ChatMessage,
  ChatMessageContentItem,
  OpenRelevantViewActionProposal,
  OpenWizardAtStepActionProposal,
  OpenWizardForTaskConfigurationActionProposal,
  PrepareExportBundleActionProposal,
  ResolveTaskTemplateSlotActionProposal,
  SetupCopilotBlockingTask,
  SetupCopilotContext,
  SetupCopilotTaskConfigurationSession,
  ChatService,
  StreamingEvent
} from 'src/app/services/chat/chat.service';
import { IThingieCreationNavigationState, WizardStep } from 'src/app/model/thingie-creation-wizard/wizard-options';
import { ITask } from 'src/app/model/thingie/task';
import { IThingie } from 'src/app/model/thingie/thingie';
import { ExperimentSelection } from 'src/app/components/dialogs/object-export-dialog/experiment-list-selector/experiment-list-selector.component';
import { DeviceService } from 'src/app/services/device/device.service';
import { GraphAgentContextService } from 'src/app/services/agent/graph-agent-context.service';
import { GraphActionExecutorService } from 'src/app/services/agent/graph-action-executor.service';
import { SetupCopilotService } from 'src/app/services/agent/setup-copilot.service';
import { ThingieImportExportService } from 'src/app/services/data/thingie-import-export.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { TaskConfigurationComponent } from 'src/app/components/task-configuration/task-configuration.component';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { CopilotSidebarService } from 'src/app/services/copilot-sidebar/copilot-sidebar.service';
import { LogService } from 'src/app/services/loggers/logger.service';
import { UINotificationService } from 'src/app/services/ui-notification/uinotification.service';
import { UserService } from 'src/app/services/user/user.service';
import { renderCopilotMessageHtml } from 'src/app/utility/copilot-message-markdown';

interface CsvAttachmentPreview {
  columns: string[];
  rows: string[][];
  hasMoreRows: boolean;
  hasMoreColumns: boolean;
  notice?: string;
}

interface LocalToolGroupDefinition {
  id: ChatLocalToolGroupId;
  label: string;
  icon: string;
  description: string;
}

interface LocalToolDefinition {
  id: ChatLocalToolId;
  groupId: ChatLocalToolGroupId;
  label: string;
  description: string;
  icon: string;
  requiresPrompt?: boolean;
  executionMode: 'local' | 'llm';
}

interface SelectedToolDispatchResult {
  handled: boolean;
  assistantMessage?: string;
  actionProposals?: ActionProposal[];
  hiddenContext?: ChatHiddenContext;
  forwardedMessage?: string;
}

const LOCAL_TOOL_GROUPS: LocalToolGroupDefinition[] = [
  { id: 'experiment', label: 'Experiment', icon: 'flask-outline', description: 'Create, duplicate, or configure experiment workflows.' },
  { id: 'graph', label: 'Graph', icon: 'stats-chart-outline', description: 'Create or modify graph tabs for the current context.' },
  { id: 'data', label: 'Data', icon: 'download-outline', description: 'Export experiment data and prepared bundles.' },
  { id: 'annotations', label: 'Annotations', icon: 'create-outline', description: 'Capture findings as annotations in DOTS.' }
];

const LOCAL_TOOLS: LocalToolDefinition[] = [
  {
    id: 'experiment.create_experiment',
    groupId: 'experiment',
    label: 'Create Experiment',
    description: 'Create or refine an experiment setup in DOTS.',
    icon: 'add-circle-outline',
    executionMode: 'local'
  },
  {
    id: 'experiment.duplicate_experiment',
    groupId: 'experiment',
    label: 'Duplicate Experiment',
    description: 'Open the duplication wizard for the current experiment.',
    icon: 'copy-outline',
    executionMode: 'local'
  },
  {
    id: 'experiment.auto_assign_devices',
    groupId: 'experiment',
    label: 'Auto-Assign Devices',
    description: 'Prepare automatic device assignment for the current experiment.',
    icon: 'hardware-chip-outline',
    executionMode: 'local'
  },
  {
    id: 'graph.create_graph',
    groupId: 'graph',
    label: 'Create Graph',
    description: 'Create a new graph tab from the current context.',
    icon: 'add-outline',
    requiresPrompt: true,
    executionMode: 'llm'
  },
  {
    id: 'graph.modify_graph',
    groupId: 'graph',
    label: 'Modify Graph',
    description: 'Edit an existing graph using the current context.',
    icon: 'options-outline',
    requiresPrompt: true,
    executionMode: 'llm'
  },
  {
    id: 'data.export_experiment_data',
    groupId: 'data',
    label: 'Export Experiment Data',
    description: 'Prepare an export bundle for the current experiment.',
    icon: 'download-outline',
    executionMode: 'local'
  },
  {
    id: 'annotations.annotate_finding',
    groupId: 'annotations',
    label: 'Annotate Finding',
    description: 'Create an annotation from the current finding.',
    icon: 'create-outline',
    requiresPrompt: true,
    executionMode: 'local'
  }
];

@Component({
  selector: 'app-copilot-sidebar',
  templateUrl: './chatbot-dialog.component.html',
  styleUrls: ['./chatbot-dialog.component.scss'],
  imports: [
    CommonModule,
    MaterialImportsModule,
    FormsModule,
    ReactiveFormsModule,
    TextFieldModule,
    IonIcon,
    TaskConfigurationComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true
})
export class ChatbotDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('messagesContainer', { read: ElementRef })
  private messagesContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('streamingReasoningContent', { read: ElementRef })
  private streamingReasoningContent: ElementRef<HTMLDivElement> | undefined;

  @ViewChild('providerSelect')
  private providerSelect: MatSelect | undefined;

  @ViewChild('modelSelect')
  private modelSelect: MatSelect | undefined;

  private sidebarService = inject(CopilotSidebarService);
  private chatService = inject(ChatService);
  private configurationService = inject(ConfigurationService);
  private graphActionExecutor = inject(GraphActionExecutorService);
  private graphAgentContextService = inject(GraphAgentContextService);
  private deviceService = inject(DeviceService);
  private setupCopilotService = inject(SetupCopilotService);
  private thingieService = inject(ThingieService);
  private thingieImportExportService = inject(ThingieImportExportService);
  private userService = inject(UserService);
  private log = inject(LogService);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private uiNotifications = inject(UINotificationService);
  private formattedMessageCache = new WeakMap<ChatMessage, string>();
  private csvAttachmentPreviewCache = new WeakMap<ChatCsvAttachment, CsvAttachmentPreview>();

  protected messages: ChatMessage[] = [];
  protected messageInput = new FormControl('');
  protected feedbackInput = new FormControl('');
  protected isLoading = false;
  protected isExporting = false;
  protected error: string | null = null;
  protected isConfigured = true;
  protected showScrollToBottom = false;
  protected lastErrorIndex: number | null = null;
  protected selectedImages: string[] = [];
  protected pendingAttachmentIndicators: ChatAttachmentIndicator[] = [];
  protected selectedLocalToolId: ChatLocalToolId | null = null;
  protected readonly localToolGroups = LOCAL_TOOL_GROUPS;
  private pendingHiddenContext: ChatHiddenContext | undefined;
  private scopedConversationHiddenContext: ChatHiddenContext | undefined;

  // Consent state
  protected consentGiven = false;
  private static readonly CONSENT_KEY = 'dots-copilot-consent';

  // Conversation rating state
  protected conversationRating = 0;
  protected showRatingPrompt = false;
  protected ratingDismissed = false;
  protected readonly starLabels = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  protected readonly exampleQuestions = [
    'How can I improve oxygen transfer in my shake flasks?',
    'My OTR curve shows a plateau - what does this mean?',
    'What fill volume and shaking speed should I use for E. coli?',
    'How do I troubleshoot slow growth in shake flask cultures?',
    'Set up a microbial shake flask experiment for E. coli with DO and biomass monitoring.',
    'Recommend a cell culture shake flask template for 6 replicates with fluorescence tracking.'
  ];

  // Bioprocessing-themed thinking messages for loading state
  protected readonly thinkingMessages: string[] = [
    'Analyzing your bioprocess question...',
    'Consulting the fermentation knowledge base...',
    'Calculating oxygen transfer coefficients...',
    'Running through cell growth models...',
    'Reviewing bioreactor parameters...',
    'Cross-referencing metabolic pathways...',
    'Optimizing the response cultivation...',
    'Checking upstream process data...',
    'Deep in microbial contemplation...',
    'Pipetting thoughts into place...',
    'Centrifuging the data...',
    'Incubating a thoughtful response...',
    'Harvesting insights from the literature...',
    'Balancing the equation of knowledge...',
    'Scaling up the analysis...',
    'Monitoring the thought fermentation...',
    'Adjusting pH of the response...',
    'Running quality control checks...'
  ];

  protected thinkingMessage = '';
  private thinkingMessageIndex = 0;
  private thinkingInterval: ReturnType<typeof setInterval> | null = null;

  // Streaming state
  protected streamingReasoning = '';
  protected streamingContent = '';
  protected isStreaming = false;
  protected showReasoning = true;
  protected currentStatus = '';
  protected expandedReasoningIndices: Set<number> = new Set();
  protected expandedMessageIndices: Set<number> = new Set();
  protected isResizingSidebar = false;
  private streamSubscription: Subscription | null = null;
  private streamingActionProposals: ActionProposal[] = [];
  protected applyingActionProposalKeys = new Set<string>();
  private activeResizePointerId: number | null = null;
  private shouldAutoScroll = true;
  private readonly longMessageCharacterThreshold = 1200;
  private readonly longMessageLineThreshold = 16;
  private readonly sidebarResizeStep = 48;
  private readonly csvPreviewRowLimit = 5;
  private readonly csvPreviewColumnLimit = 6;
  private readonly csvPreviewCharacterLimit = 100_000;

  // Conversation management state
  protected conversations: IChatConversation[] = [];
  protected currentConversationId: string | null = null;
  protected currentConversationScope: IChatConversationScope | null = null;
  protected promptAuditTrail: IPromptAuditEntry[] = [];
  protected showSidebar = false;
  protected isLoadingConversations = false;
  protected editingConversationId: string | null = null;
  protected editingTitle = '';
  private forceDirectMode = false;
  private saveSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private readonly subs = new Subscription();

  // Workflow mode (ChatKit) state
  protected useWorkflowMode = false;
  protected chatkitReady = false;
  protected chatkitError: string | null = null;
  private currentChatkitPromptAudit: IPromptAuditEntry | null = null;
  private currentChatkitThreadId: string | null = null;
  private chatkitSyncSubject = new Subject<void>();
  @ViewChild('chatkitElement', { read: ElementRef })
  private chatkitElement: ElementRef | undefined;

  // Provider + model selection state
  protected selectedProvider: AIProvider = 'openai';
  protected selectedModel = '';
  protected providerLocked = false;
  protected modelCatalogs: IModelCatalogs | null = null;
  protected ollamaVisionCatalog: IProviderModelCatalog | null = null;
  protected readonly availableProviders: { value: AIProvider; label: string }[] = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'gemini', label: 'Gemini' },
    { value: 'ollama', label: 'Ollama' }
  ];

  // Image lightbox state
  protected lightboxImage: string | null = null;

  public async ngOnInit(): Promise<void> {
    const [openaiOk, anthropicOk, geminiOk, ollamaOk] = await Promise.all([
      this.chatService.hasApiKey('openai'),
      this.chatService.hasApiKey('anthropic'),
      this.chatService.hasApiKey('gemini'),
      this.chatService.hasApiKey('ollama')
    ]);
    this.isConfigured = openaiOk || anthropicOk || geminiOk || ollamaOk;

    if (openaiOk) {
      this.selectedProvider = 'openai';
    } else if (anthropicOk) {
      this.selectedProvider = 'anthropic';
    } else if (geminiOk) {
      this.selectedProvider = 'gemini';
    } else if (ollamaOk) {
      this.selectedProvider = 'ollama';
    }

    this.consentGiven = localStorage.getItem(ChatbotDialogComponent.CONSENT_KEY) === 'true';

    // Check if workflow mode is enabled
    try {
      const settings = await this.chatService.getSettings();
      this.useWorkflowMode = !!settings.workflowId && this.selectedProvider === 'openai';
    } catch {
      this.useWorkflowMode = false;
    }

    await this.loadModelCatalogs();
    await this.loadDefaultModel();

    void this.loadConversations();

    if (this.useWorkflowMode) {
      setTimeout(() => this.initializeChatKit(), 0);
    }

    // Set up auto-save with debounce
    this.subs.add(this.saveSubject.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.autoSaveConversation();
    }));

    // Set up ChatKit thread sync with debounce
    this.subs.add(this.chatkitSyncSubject.pipe(
      debounceTime(1000),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      void this.syncChatkitConversation();
    }));

    this.subs.add(this.sidebarService.pendingNewConversation$.pipe(
      filter((payload): payload is {
        imageDataUrl: string;
        scope: IChatConversationScope | null;
        hiddenContext?: ChatHiddenContext;
        attachmentIndicators?: ChatAttachmentIndicator[];
      } => payload !== null),
      takeUntil(this.destroy$)
    ).subscribe((payload) => {
      this.sidebarService.consumePendingNewConversation();
      void this.startNewConversation(payload.scope, {
        imageDataUrl: payload.imageDataUrl,
        hiddenContext: payload.hiddenContext,
        attachmentIndicators: payload.attachmentIndicators
      });
    }));

    this.subs.add(this.sidebarService.pendingImage$.pipe(
      filter((img): img is string => img !== null),
      takeUntil(this.destroy$)
    ).subscribe((imageDataUrl) => {
      this.onNewConversation();
      this.selectedImages.push(imageDataUrl);
      this.sidebarService.consumePendingImage();
    }));

    this.subs.add(combineLatest([
      this.sidebarService.pendingScope$,
      this.sidebarService.pendingScopeContext$
    ])
      .pipe(
        filter(([scope]) => scope !== null),
        takeUntil(this.destroy$)
      )
      .subscribe(([scope, hiddenContext]) => {
        this.sidebarService.consumePendingScope();
        void this.openScopedConversation(scope as IChatConversationScope, hiddenContext ?? undefined);
      }));

    this.subs.add(this.sidebarService.pendingConversationId$.pipe(
      filter((conversationId): conversationId is string => conversationId !== null),
      takeUntil(this.destroy$)
    ).subscribe((conversationId) => {
      this.sidebarService.consumePendingConversationId();
      void this.openConversationById(conversationId);
    }));
  }

  public ngAfterViewInit(): void {
    this.messageInput.updateValueAndValidity();
    setTimeout(() => this.setupScrollListener(), 0);
  }

  private setupScrollListener(): void {
    if (this.messagesContainer) {
      const element = this.messagesContainer.nativeElement;
      element.removeEventListener('scroll', this.handleMessagesScroll);
      element.addEventListener('scroll', this.handleMessagesScroll);
      this.updateScrollState(element);
    }
  }

  private initializeChatKit(): void {
    const el = this.chatkitElement?.nativeElement;
    if (!el || typeof el.setOptions !== 'function') {
      this.log.warn('ChatKit web component not ready, retrying...');
      setTimeout(() => this.initializeChatKit(), 500);
      return;
    }

    const configService = this.configurationService;
    const log = this.log;
    const component = this;
    const appendCurrentChatkitPromptAudit = (): void => {
      if (!component.currentChatkitPromptAudit || component.hasPromptAuditEntry(component.currentChatkitPromptAudit)) {
        return;
      }
      component.promptAuditTrail = [...component.promptAuditTrail, component.currentChatkitPromptAudit];
    };

    el.setOptions({
      api: {
        async getClientSecret(_: string | null): Promise<string> {
          try {
            const session = await configService.createChatkitSession();
            component.currentChatkitPromptAudit = session.promptAudit ?? null;
            appendCurrentChatkitPromptAudit();
            return session.client_secret;
          } catch (err) {
            log.error('Failed to create ChatKit session', err);
            throw err;
          }
        }
      },
      theme: 'light'
    });

    el.addEventListener('chatkit.thread.change', (e: CustomEvent<{ threadId: string | null }>) => {
      this.currentChatkitThreadId = e.detail?.threadId ?? null;
      appendCurrentChatkitPromptAudit();
      this.log.debug('ChatKit thread changed', { threadId: this.currentChatkitThreadId });
    });

    el.addEventListener('chatkit.response.end', () => {
      if (this.currentChatkitThreadId) {
        this.chatkitSyncSubject.next();
      }
    });

    el.addEventListener('chatkit.error', (e: CustomEvent<{ error: Error }>) => {
      this.chatkitError = e.detail?.error?.message || 'An error occurred';
      this.log.error('ChatKit error', e.detail?.error);
    });

    this.chatkitReady = true;
    this.log.info('ChatKit web component initialized');
  }

  private async syncChatkitConversation(): Promise<void> {
    if (!this.currentChatkitThreadId) {
      return;
    }

    try {
      const syncedConversation = await this.configurationService.syncChatkitThread(
        this.currentChatkitThreadId,
        this.promptAuditTrail,
        this.currentConversationScope
      );
      const saved = this.isScopedConversation(syncedConversation)
        ? await firstValueFrom(this.chatService.saveConversation(syncedConversation))
        : syncedConversation;
      const existingIndex = this.conversations.findIndex(c => c.chatkitThreadId === saved.chatkitThreadId);
      if (existingIndex !== -1) {
        this.conversations[existingIndex] = saved;
      } else {
        this.conversations.unshift(saved);
      }
      this.conversations.sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    } catch (err) {
      this.log.error('Failed to sync ChatKit conversation', err);
    }
  }

  private async loadDefaultModel(): Promise<void> {
    try {
      switch (this.selectedProvider) {
        case 'openai': {
          const s = await this.configurationService.getOpenaiSettings();
          this.selectedModel = s.model?.trim()
            || getFirstVisionModelId(this.modelCatalogs?.openai)
            || 'gpt-5.4';
          break;
        }
        case 'anthropic': {
          const s = await this.configurationService.getAnthropicSettings();
          this.selectedModel = s.model?.trim()
            || getFirstVisionModelId(this.modelCatalogs?.anthropic)
            || 'claude-sonnet-4-6';
          break;
        }
        case 'gemini': {
          const s = await this.configurationService.getGeminiSettings();
          this.selectedModel = s.model?.trim()
            || getFirstVisionModelId(this.modelCatalogs?.gemini)
            || 'gemini-2.5-flash';
          break;
        }
        case 'ollama': {
          const s = await this.configurationService.getOllamaSettings();
          this.selectedModel = s.model?.trim()
            || getFirstVisionModelId(this.ollamaVisionCatalog ?? this.modelCatalogs?.ollama)
            || 'llama3.2-vision';
          break;
        }
      }
    } catch {
      this.selectedModel = '';
    }
  }

  private async loadModelCatalogs(): Promise<void> {
    const [modelCatalogsResult, ollamaCatalogResult] = await Promise.allSettled([
      this.configurationService.getModelCatalogs(),
      this.configurationService.getOllamaVisionModelCatalog()
    ]);

    if (modelCatalogsResult.status === 'fulfilled') {
      this.modelCatalogs = modelCatalogsResult.value;
    } else {
      this.modelCatalogs = null;
      this.log.warn('Failed to load provider model catalogs', modelCatalogsResult.reason);
    }

    if (ollamaCatalogResult.status === 'fulfilled') {
      this.ollamaVisionCatalog = ollamaCatalogResult.value;
    } else {
      this.ollamaVisionCatalog = null;
      this.log.warn('Failed to load Ollama vision model catalog', ollamaCatalogResult.reason);
    }
  }

  protected getAvailableModelOptions(): IProviderModelOption[] {
    const modelOptions = this.selectedProvider === 'ollama'
      ? (this.ollamaVisionCatalog?.models ?? [])
      : getVisionModelOptions(this.getSelectedProviderCatalog());
    return ensureSelectedModelOption(
      modelOptions,
      this.selectedModel
    );
  }

  private getSelectedProviderCatalog(): IProviderModelCatalog | null {
    if (!this.modelCatalogs) {
      return null;
    }
    switch (this.selectedProvider) {
      case 'openai':
        return this.modelCatalogs.openai;
      case 'anthropic':
        return this.modelCatalogs.anthropic;
      case 'gemini':
        return this.modelCatalogs.gemini;
      case 'ollama':
        return this.ollamaVisionCatalog ?? this.modelCatalogs.ollama;
    }
  }

  protected async onProviderChange(): Promise<void> {
    this.closeProviderSelectors();
    if (!this.providerLocked) {
      await this.loadDefaultModel();
    }
  }

  protected onModelChange(): void {
    this.closeProviderSelectors();
  }

  protected onExampleQuestionClick(question: string): void {
    this.messageInput.setValue(question);
    this.messageInput.markAsTouched();
    // Focus on textarea
    setTimeout(() => {
      const textarea = document.querySelector('.message-input textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(question.length, question.length);
      }
    }, 0);
  }

  protected async onSendMessage(): Promise<void> {
    const messageText = this.messageInput.value?.trim() || '';
    const hasImages = this.selectedImages.length > 0;
    const hasAttachmentIndicators = this.pendingAttachmentIndicators.length > 0;
    const hasSelectedLocalTool = this.getPendingSelectedLocalToolId() !== null;

    if ((!this.messageInput.valid || this.isLoading)
      || (!messageText && !hasImages && !hasAttachmentIndicators && !hasSelectedLocalTool)
      || !this.canSendWithSelectedTool(messageText)) {
      return;
    }

    this.closeProviderSelectors();
    const userMessage = messageText;
    const imagesToSend = [...this.selectedImages];
    const mergedHiddenContext = this.mergeHiddenContexts(
      this.getConversationLevelHiddenContext(),
      this.pendingHiddenContext
    );
    const selectedTool = this.getPendingSelectedTool() ?? mergedHiddenContext?.selectedTool;
    let hiddenContextToSend = this.withSelectedTool(mergedHiddenContext, selectedTool);
    const selectedToolResult = await this.dispatchSelectedToolMessage(
      userMessage,
      hiddenContextToSend,
      selectedTool
    );
    let providerMessage = userMessage;

    if (selectedToolResult.hiddenContext !== undefined) {
      hiddenContextToSend = selectedToolResult.hiddenContext;
    }
    if (selectedToolResult.forwardedMessage !== undefined) {
      providerMessage = selectedToolResult.forwardedMessage;
    }

    if (!selectedToolResult.handled) {
      const hiddenContextWithSetup = await this.setupCopilotService.enrichFullSetupContextForLlm(
        userMessage,
        hiddenContextToSend
      );
      hiddenContextToSend = await this.graphAgentContextService.enrichHiddenContextForMessage(
        userMessage,
        hiddenContextWithSetup ?? hiddenContextToSend
      );
      hiddenContextToSend = this.withSelectedTool(hiddenContextToSend, selectedTool);
    }

    const attachmentIndicatorsToSend = [...this.pendingAttachmentIndicators];
    const immediateActionProposals = this.graphAgentContextService.getImmediateActionProposals(
      userMessage,
      hiddenContextToSend
    );
    this.clearComposerStateAfterSend();
    this.error = null;
    this.lastErrorIndex = null;

    let userContent: ChatMessage['content'];
    if (imagesToSend.length > 0) {
      const contentItems: ChatMessageContentItem[] = [];
      if (userMessage) {
        contentItems.push({ type: 'text', text: userMessage });
      }
      for (const imageDataUrl of imagesToSend) {
        contentItems.push({
          type: 'image_url',
          image_url: { url: imageDataUrl }
        });
      }
      userContent = contentItems;
    } else {
      userContent = userMessage;
    }

    const userChatMessage: ChatMessage = {
      role: 'user',
      content: userContent,
      hiddenContext: this.cloneHiddenContext(hiddenContextToSend, { stripExperimentSamplePayloads: true }),
      attachmentIndicators: attachmentIndicatorsToSend.length > 0
        ? attachmentIndicatorsToSend
        : undefined
    };
    this.messages.push(userChatMessage);
    this.shouldAutoScroll = true;
    this.scrollToBottom();

    if (selectedToolResult.handled) {
      this.streamingActionProposals = [];
      this.streamingReasoning = '';
      this.streamingContent = '';
      this.currentStatus = '';
      const localAssistantMessage: ChatMessage = {
        role: 'assistant',
        content: selectedToolResult.assistantMessage ?? 'I prepared the requested tool action.',
        actionProposals: selectedToolResult.actionProposals,
        hiddenContext: this.cloneHiddenContext(selectedToolResult.hiddenContext, { stripExperimentSamplePayloads: true })
      };
      this.messages.push(localAssistantMessage);
      if (this.shouldAutoScroll) {
        this.scrollToLastMessageStart();
      }
      this.saveSubject.next();
      this.updateRatingPromptVisibility();
      return;
    }

    this.streamingActionProposals = immediateActionProposals;
    this.isLoading = true;
    this.isStreaming = true;
    this.streamingReasoning = '';
    this.streamingContent = '';
    this.currentStatus = this.shouldUseProviderPythonAnalysis(hiddenContextToSend)
      ? 'Analyzing chart data with Python...'
      : '';
    this.showReasoning = true;
    this.providerLocked = true;

    this.streamSubscription = this.chatService
      .sendMessage(
        providerMessage,
        imagesToSend,
        this.messages.slice(0, -1),
        this.selectedProvider,
        this.selectedModel || undefined,
        hiddenContextToSend
      )
      .subscribe({
        next: (event: StreamingEvent) => {
          this.handleStreamingEvent(event);
        },
        error: (error: unknown) => {
          this.error = error instanceof Error ? error.message : 'Failed to send message';
          this.lastErrorIndex = this.messages.length - 1;
          this.log.error('Failed to send message', error);
          this.isLoading = false;
          this.isStreaming = false;
          this.currentStatus = '';
        },
        complete: () => {
          this.isLoading = false;
          this.isStreaming = false;
          this.currentStatus = '';
        }
      });
  }

  private handleStreamingEvent(event: StreamingEvent): void {
    switch (event.type) {
      case 'reasoning_delta':
        this.streamingReasoning += event.delta;
        this.scrollToBottomIfNeeded();
        this.scrollReasoningContentToBottomIfNeeded();
        break;

      case 'text_delta':
        this.streamingContent += event.delta;
        this.scrollToBottomIfNeeded();
        break;

      case 'status':
        switch (event.status) {
          case 'web_search_in_progress':
            this.currentStatus = 'Searching the web...';
            break;
          case 'web_search_completed':
            this.currentStatus = '';
            break;
          case 'reasoning_started':
            this.currentStatus = 'Thinking...';
            break;
          case 'response_started':
            this.currentStatus = '';
            break;
          case 'analysis_tool_in_progress':
            this.currentStatus = 'Analyzing chart data with Python...';
            break;
        }
        break;

      case 'action_proposal':
        this.streamingActionProposals = this.mergeActionProposals(
          this.streamingActionProposals,
          event.actions
        );
        break;

      case 'completed': {
        const fallbackActionProposals = this.shouldParseTextActionProposals()
          ? this.parseActionProposalsFromMessage(event.message)
          : [];
        const mergedActionProposals = this.mergeActionProposals(
          event.message.actionProposals ?? [],
          this.mergeActionProposals(this.streamingActionProposals, fallbackActionProposals)
        );
        const completedMessage: ChatMessage = mergedActionProposals.length > 0
          ? {
            ...event.message,
            actionProposals: mergedActionProposals
          }
          : event.message;
        // Add the completed message to the conversation
        this.messages.push(completedMessage);
        if (event.promptAudit) {
          this.promptAuditTrail = [
            ...this.promptAuditTrail,
            { ...event.promptAudit, messageIndex: this.messages.length - 1 }
          ];
        }
        this.streamingActionProposals = [];
        this.streamingReasoning = '';
        this.streamingContent = '';
        // If the message has reasoning, add its index to expanded set
        if (completedMessage.reasoning) {
          this.expandedReasoningIndices.add(this.messages.length - 1);
        }
        if (this.shouldAutoScroll) {
          this.scrollToLastMessageStart();
        }
        // Trigger auto-save
        this.saveSubject.next();
        // Show rating prompt if conversation has not been rated yet
        this.updateRatingPromptVisibility();
        break;
      }

      case 'error':
        this.error = event.error;
        this.lastErrorIndex = this.messages.length - 1;
        this.isLoading = false;
        this.isStreaming = false;
        this.currentStatus = '';
        this.streamingReasoning = '';
        this.streamingContent = '';
        this.streamingActionProposals = [];
        break;
    }
  }

  protected toggleMessageReasoning(index: number): void {
    if (this.expandedReasoningIndices.has(index)) {
      this.expandedReasoningIndices.delete(index);
    } else {
      this.expandedReasoningIndices.add(index);
    }
  }

  protected isReasoningExpanded(index: number): boolean {
    return this.expandedReasoningIndices.has(index);
  }

  protected toggleStreamingReasoning(): void {
    this.showReasoning = !this.showReasoning;
  }

  protected toggleMessageExpanded(index: number): void {
    if (this.expandedMessageIndices.has(index)) {
      this.expandedMessageIndices.delete(index);
    } else {
      this.expandedMessageIndices.add(index);
    }
  }

  protected isMessageExpanded(index: number): boolean {
    return this.expandedMessageIndices.has(index);
  }

  protected shouldShowMessageToggle(message: ChatMessage): boolean {
    if (message.role !== 'assistant') {
      return false;
    }

    const messageText = this.getMessageText(message);
    const lineCount = messageText.split(/\r?\n/).length;
    return messageText.length > this.longMessageCharacterThreshold || lineCount > this.longMessageLineThreshold;
  }

  protected isMessageCollapsed(message: ChatMessage, index: number): boolean {
    return this.shouldShowMessageToggle(message) && !this.isMessageExpanded(index);
  }

  protected onSidebarResizeStart(event: PointerEvent): void {
    if (typeof window === 'undefined' || window.innerWidth <= 480) {
      return;
    }

    this.isResizingSidebar = true;
    this.activeResizePointerId = event.pointerId;
    document.body.classList.add('copilot-sidebar-resizing');
    event.preventDefault();
  }

  protected onSidebarResizeKeydown(event: KeyboardEvent): void {
    if (typeof window === 'undefined' || window.innerWidth <= 480) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.sidebarService.setSidebarWidth(this.sidebarService.sidebarWidth + this.sidebarResizeStep);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.sidebarService.setSidebarWidth(this.sidebarService.sidebarWidth - this.sidebarResizeStep);
    }
  }

  protected onRetryMessage(): void {
    if (this.lastErrorIndex !== null && this.lastErrorIndex < this.messages.length) {
      const failedMessage = this.messages[this.lastErrorIndex];
      if (failedMessage.role === 'user') {
        // Remove the failed user message and retry
        this.messages.splice(this.lastErrorIndex);
        const messageText = this.getMessageText(failedMessage);
        const messageImages = this.getMessageImages(failedMessage);
        const hiddenContext = this.getMessageHiddenContext(failedMessage);
        const attachmentIndicators = this.getMessageAttachmentIndicators(failedMessage);
        this.messageInput.setValue(messageText);
        this.selectedImages = [...messageImages];
        this.pendingHiddenContext = hiddenContext;
        this.pendingAttachmentIndicators = [...attachmentIndicators];
        this.selectedLocalToolId = hiddenContext?.selectedTool?.toolId ?? null;
        void this.onSendMessage();
      }
    }
  }

  protected onClearConversation(): void {
    // Cancel any active stream
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = null;
    }

    // If we have an active conversation with messages, delete it
    if (this.currentConversationId) {
      this.subs.add(this.chatService.deleteConversation(this.currentConversationId).subscribe({
        next: () => {
          this.conversations = this.conversations.filter(c => c.id !== this.currentConversationId);
        },
        error: (error: unknown) => {
          this.log.error('Failed to delete conversation on clear', error);
        }
      }));
    }

    this.currentConversationId = null;
    this.messages = [];
    this.promptAuditTrail = [];
    this.currentChatkitPromptAudit = null;
    this.formattedMessageCache = new WeakMap<ChatMessage, string>();
    this.error = null;
    this.lastErrorIndex = null;
    this.messageInput.setValue('');
    this.selectedImages = [];
    this.pendingHiddenContext = undefined;
    this.scopedConversationHiddenContext = undefined;
    this.pendingAttachmentIndicators = [];
    this.selectedLocalToolId = null;
    this.forceDirectMode = false;
    this.streamingReasoning = '';
    this.streamingContent = '';
    this.isStreaming = false;
    this.isLoading = false;
    this.currentStatus = '';
    this.shouldAutoScroll = true;
    this.expandedReasoningIndices.clear();
    this.expandedMessageIndices.clear();
  }

  protected async onExportConversation(): Promise<void> {
    if (this.messages.length === 0 || this.isExporting) {
      return;
    }

    this.isExporting = true;

    try {
      const container = this.messagesContainer.nativeElement;

      // Store original styles to restore later
      const originalOverflow = container.style.overflow;
      const originalMaxHeight = container.style.maxHeight;
      const originalHeight = container.style.height;

      // Add exporting class to hide UI elements that shouldn't be in the image
      container.classList.add('exporting');

      // Temporarily modify container styles for full capture
      container.style.overflow = 'visible';
      container.style.maxHeight = 'none';
      container.style.height = 'auto';

      // Create header element and prepend it to the container
      const header = document.createElement('div');
      header.className = 'export-header-temp';
      header.style.cssText = 'display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1rem; margin-bottom: 0.75rem; border-bottom: 2px solid #1976d2; font-family: Roboto, "Helvetica Neue", sans-serif; background-color: white;';

      const title = document.createElement('span');
      title.style.cssText = 'font-size: 1.25rem; font-weight: 600; color: #1976d2;';
      title.textContent = 'DOTS Copilot';

      const timestamp = document.createElement('span');
      timestamp.style.cssText = 'font-size: 0.875rem; color: rgba(0, 0, 0, 0.6);';
      timestamp.textContent = new Date().toLocaleString();

      header.appendChild(title);
      header.appendChild(timestamp);
      container.insertBefore(header, container.firstChild);

      // Generate the image directly from the container
      const blob = await domtoimage.toBlob(container, {
        bgcolor: 'white',
        style: {
          'font-family': 'Roboto, "Helvetica Neue", sans-serif'
        },
        filter: (node: Node) => {
          // Filter out elements that shouldn't be in the export
          if (node instanceof HTMLElement) {
            if (node.classList.contains('scroll-to-bottom-button') ||
                node.classList.contains('copy-button') ||
                node.classList.contains('welcome-message')) {
              return false;
            }
          }
          return true;
        }
      });

      // Clean up: remove header and restore styles
      container.removeChild(header);
      container.classList.remove('exporting');
      container.style.overflow = originalOverflow;
      container.style.maxHeight = originalMaxHeight;
      container.style.height = originalHeight;

      // Generate filename with timestamp
      const dateStr = new Date().toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, 19);
      const filename = `dots-copilot-conversation-${dateStr}.png`;

      // Download the image
      saveAs(blob, filename);
      this.log.info('Conversation exported successfully');
    } catch (error) {
      this.log.error('Failed to export conversation', error);
      this.error = 'Failed to export conversation as image';
    } finally {
      this.isExporting = false;
      // Ensure class is removed even if there was an error
      const container = this.messagesContainer?.nativeElement;
      if (container) {
        container.classList.remove('exporting');
        // Also try to remove header if it exists
        const tempHeader = container.querySelector('.export-header-temp');
        if (tempHeader) {
          container.removeChild(tempHeader);
        }
      }
    }
  }

  protected onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      for (const file of files) {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            if (result) {
              this.selectedImages.push(result);
            }
          };
          reader.readAsDataURL(file);
        }
      }
      // Reset input so same file can be selected again
      input.value = '';
    }
  }

  protected onRemoveImage(index: number): void {
    this.selectedImages.splice(index, 1);
  }

  protected onRemovePendingAttachmentIndicator(kind: ChatAttachmentIndicator['kind']): void {
    this.pendingAttachmentIndicators = this.pendingAttachmentIndicators.filter(indicator => indicator.kind !== kind);
    if (kind === 'chart_data') {
      this.pendingHiddenContext = undefined;
    }
  }

  protected shouldShowPendingAnalysisIndicator(): boolean {
    return this.hasChartAnalysis(this.pendingHiddenContext);
  }

  protected getPendingAnalysisIndicatorLabel(): string {
    return this.shouldUseProviderPythonAnalysis(this.pendingHiddenContext)
      ? 'Python analysis enabled'
      : 'CSV fallback mode';
  }

  protected getPendingAnalysisIndicatorIcon(): string {
    return this.shouldUseProviderPythonAnalysis(this.pendingHiddenContext)
      ? 'analytics-outline'
      : 'information-circle-outline';
  }

  protected getMessageText(message: ChatMessage): string {
    if (typeof message.content === 'string') {
      return message.content;
    }
    const textItem = message.content.find(item => item.type === 'text');
    return textItem?.text || '';
  }

  protected getMessageImages(message: ChatMessage): string[] {
    if (typeof message.content === 'string') {
      return [];
    }
    return message.content
      .filter(item => item.type === 'image_url')
      .map(item => item.image_url?.url || '')
      .filter(url => url.length > 0);
  }

  protected getCsvAttachments(message: ChatMessage): ChatCsvAttachment[] {
    return (message.attachments ?? [])
      .filter((attachment): attachment is ChatCsvAttachment => attachment.kind === 'csv');
  }

  protected getCsvAttachmentPreview(attachment: ChatCsvAttachment): CsvAttachmentPreview {
    const cachedPreview = this.csvAttachmentPreviewCache.get(attachment);
    if (cachedPreview !== undefined) {
      return cachedPreview;
    }

    const preview = this.buildCsvAttachmentPreview(attachment);
    this.csvAttachmentPreviewCache.set(attachment, preview);
    return preview;
  }

  protected getCsvAttachmentSizeLabel(attachment: ChatCsvAttachment): string {
    const byteCount = new Blob([attachment.csvContent], { type: attachment.mimeType || 'text/csv' }).size;
    return this.formatByteCount(byteCount);
  }

  protected getCsvAttachmentSourceLabel(attachment: ChatCsvAttachment): string {
    const provider = attachment.source?.provider;
    if (!provider) {
      return 'Generated CSV';
    }

    switch (provider) {
      case 'openai':
        return 'Generated by OpenAI';
      case 'anthropic':
        return 'Generated by Anthropic';
      case 'gemini':
        return 'Generated by Gemini';
      case 'ollama':
        return 'Generated by Ollama';
      default:
        return 'Generated CSV';
    }
  }

  protected onDownloadCsvAttachment(attachment: ChatCsvAttachment): void {
    const blob = new Blob([attachment.csvContent], {
      type: attachment.mimeType || 'text/csv;charset=utf-8'
    });
    saveAs(blob, attachment.filename);
  }

  protected getMessageAttachmentIndicators(message: ChatMessage): ChatAttachmentIndicator[] {
    return [...(message.attachmentIndicators ?? [])];
  }

  protected shouldShowMessageAnalysisIndicator(message: ChatMessage): boolean {
    return this.hasChartAnalysis(message.hiddenContext);
  }

  protected getMessageAnalysisIndicatorLabel(message: ChatMessage): string {
    return this.shouldUseProviderPythonAnalysis(message.hiddenContext)
      ? 'Python analysis requested'
      : 'CSV fallback mode';
  }

  protected getMessageAnalysisIndicatorIcon(message: ChatMessage): string {
    return this.shouldUseProviderPythonAnalysis(message.hiddenContext)
      ? 'analytics-outline'
      : 'information-circle-outline';
  }

  private getMessageHiddenContext(message: ChatMessage): ChatHiddenContext | undefined {
    return this.cloneHiddenContext(message.hiddenContext);
  }

  private buildCsvAttachmentPreview(attachment: ChatCsvAttachment): CsvAttachmentPreview {
    const csvContent = attachment.csvContent.trim();
    if (!csvContent) {
      return {
        columns: [],
        rows: [],
        hasMoreRows: false,
        hasMoreColumns: false,
        notice: 'CSV is empty.'
      };
    }

    const parsedPreview = this.parseCsvAttachmentPreview(csvContent);
    if (parsedPreview.rows.length === 0) {
      return {
        columns: [],
        rows: [],
        hasMoreRows: false,
        hasMoreColumns: false,
        notice: parsedPreview.notice || 'Preview unavailable.'
      };
    }

    const headerRow = parsedPreview.rows[0];
    const dataRows = parsedPreview.rows.slice(1, this.csvPreviewRowLimit + 1);
    const previewColumnCount = Math.max(
      1,
      Math.min(
        this.csvPreviewColumnLimit,
        parsedPreview.rows.reduce((maxColumnCount, row) => Math.max(maxColumnCount, row.length), 0)
      )
    );
    const hasMoreColumns = parsedPreview.rows.some(row => row.length > previewColumnCount);
    const columns = Array.from({ length: previewColumnCount }, (_, index) => {
      const headerValue = headerRow[index]?.trim();
      return headerValue || `Column ${index + 1}`;
    });
    const rows = dataRows.map(row => Array.from({ length: previewColumnCount }, (_, index) => row[index] ?? ''));

    const notices: string[] = [];
    if (parsedPreview.notice) {
      notices.push(parsedPreview.notice);
    }
    if (parsedPreview.hasMoreRows) {
      notices.push(`Showing the first ${this.csvPreviewRowLimit} data rows.`);
    }
    if (hasMoreColumns) {
      notices.push(`Showing the first ${previewColumnCount} columns.`);
    }

    return {
      columns,
      rows,
      hasMoreRows: parsedPreview.hasMoreRows,
      hasMoreColumns,
      notice: notices.length > 0 ? notices.join(' ') : undefined
    };
  }

  private parseCsvAttachmentPreview(csvContent: string): {
    rows: string[][];
    hasMoreRows: boolean;
    notice?: string;
  } {
    const truncatedContent = csvContent.slice(0, this.csvPreviewCharacterLimit);
    const parseLimitReached = truncatedContent.length < csvContent.length;
    const rows: string[][] = [];
    const maxRowsToCapture = this.csvPreviewRowLimit + 2;
    let currentRow: string[] = [];
    let currentCell = '';
    let index = 0;
    let inQuotes = false;
    let hasMoreRows = false;

    const pushCell = (): void => {
      currentRow.push(currentCell);
      currentCell = '';
    };

    const pushRow = (): void => {
      pushCell();
      rows.push(currentRow);
      currentRow = [];
      if (rows.length > maxRowsToCapture) {
        rows.pop();
        hasMoreRows = true;
      }
    };

    while (index < truncatedContent.length && !hasMoreRows) {
      const character = truncatedContent[index];
      if (inQuotes) {
        if (character === '"') {
          if (truncatedContent[index + 1] === '"') {
            currentCell += '"';
            index += 2;
            continue;
          }
          inQuotes = false;
        } else {
          currentCell += character;
        }
        index++;
        continue;
      }

      if (character === '"') {
        inQuotes = true;
      } else if (character === ',') {
        pushCell();
      } else if (character === '\n') {
        pushRow();
      } else if (character === '\r') {
        pushRow();
        if (truncatedContent[index + 1] === '\n') {
          index++;
        }
      } else {
        currentCell += character;
      }
      index++;
    }

    if (!hasMoreRows && (currentCell.length > 0 || currentRow.length > 0)) {
      pushRow();
    }

    const notices: string[] = [];
    if (parseLimitReached) {
      notices.push(`Preview limited to the first ${this.formatByteCount(this.csvPreviewCharacterLimit)} of CSV content.`);
    }
    if (inQuotes) {
      notices.push('CSV preview may be incomplete because the file ended inside a quoted value.');
    }

    return {
      rows,
      hasMoreRows,
      notice: notices.length > 0 ? notices.join(' ') : undefined
    };
  }

  private formatByteCount(byteCount: number): string {
    if (byteCount < 1024) {
      return `${byteCount} B`;
    }
    if (byteCount < 1024 * 1024) {
      return `${(byteCount / 1024).toFixed(1)} KB`;
    }
    return `${(byteCount / (1024 * 1024)).toFixed(1)} MB`;
  }

  protected onCopyMessage(message: ChatMessage): void {
    const text = this.getMessageText(message);
    navigator.clipboard.writeText(text)
      .then(() => {
        this.log.info('Message copied to clipboard');
      })
      .catch((err) => {
        this.log.error('Failed to copy message', err);
      });
  }

  protected onScrollToBottom(): void {
    this.shouldAutoScroll = true;
    this.scrollToBottom();
    this.showScrollToBottom = false;
  }

  protected onDismissError(): void {
    this.error = null;
    this.lastErrorIndex = null;
  }

  protected formatMessage(message: ChatMessage): string {
    const cachedMarkup = this.formattedMessageCache.get(message);
    if (cachedMarkup !== undefined) {
      return cachedMarkup;
    }

    const renderedMarkup = this.renderMessageContent(message.content, message.role);
    this.formattedMessageCache.set(message, renderedMarkup);
    return renderedMarkup;
  }

  protected formatStreamingMessage(content: string): string {
    return this.renderMessageContent(content, 'assistant');
  }

  private renderMessageContent(content: ChatMessage['content'], role: 'user' | 'assistant' | 'system'): string {
    let textContent = '';
    if (typeof content === 'string') {
      textContent = content;
    } else {
      const textItem = content.find(item => item.type === 'text');
      textContent = textItem?.text || '';
    }

    return renderCopilotMessageHtml(this.sanitizer, textContent, role);
  }

  protected onAcceptConsent(): void {
    this.consentGiven = true;
    localStorage.setItem(ChatbotDialogComponent.CONSENT_KEY, 'true');
  }

  protected onRateMessage(index: number, rating: 'up' | 'down'): void {
    const message = this.messages[index];
    if (message.rating === rating) {
      message.rating = undefined;
    } else {
      message.rating = rating;
    }
    this.saveSubject.next();
  }

  protected onSetConversationRating(rating: number): void {
    this.conversationRating = rating;
  }

  protected onSubmitRating(): void {
    if (this.conversationRating > 0 && this.currentConversationId) {
      const existing = this.conversations.find(c => c.id === this.currentConversationId);
      if (existing) {
        existing.rating = this.conversationRating;
        existing.feedback = this.feedbackInput.value?.trim() || undefined;
        existing.updatedAt = new Date().toISOString();
        this.subs.add(this.chatService.saveConversation(existing).subscribe({
          next: () => {
            this.log.info('Conversation rating saved');
            this.showRatingPrompt = false;
          },
          error: (err: unknown) => {
            this.log.error('Failed to save conversation rating', err);
          }
        }));
      }
    }
  }

  protected onDismissRating(): void {
    this.ratingDismissed = true;
    this.showRatingPrompt = false;
  }

  protected async onExportAllConversations(): Promise<void> {
    if (this.conversations.length === 0) {
      return;
    }
    try {
      const user = await this.userService.getCurrentUser();
      const exportData: CopilotExport = {
        formatVersion: 1,
        exportedAt: new Date().toISOString(),
        exportedBy: {
          username: user.username || 'unknown',
          displayName: user.displayName || user.username || 'unknown',
          instanceUrl: window.location.origin
        },
        conversations: this.conversations
      };
      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const dateStr = new Date().toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, 19);
      saveAs(blob, `dots-copilot-export-${dateStr}.json`);
      this.log.info('All conversations exported');
    } catch (err) {
      this.log.error('Failed to export conversations', err);
      this.error = 'Failed to export conversations';
    }
  }

  protected onClose(): void {
    this.sidebarService.close();
  }

  protected getCurrentScopeTitle(): string {
    if (!this.currentConversationScope) {
      return 'Private chat';
    }

    switch (this.currentConversationScope.scopeKind) {
      case 'experiment':
        return 'Shared experiment chat';
      case 'replicate':
        return 'Shared replicate chat';
      default:
        return 'Private chat';
    }
  }

  protected isScopedConversation(conversation: IChatConversation): boolean {
    return conversation.sharingMode === 'replicate_access'
      && (conversation.linkedThingieIds?.length ?? 0) > 0;
  }

  protected isWorkflowModeActive(): boolean {
    return this.useWorkflowMode
      && !this.forceDirectMode
      && this.currentConversationScope === null
      && this.currentConversationId === null;
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.onSendMessage();
    }
  }

  protected getToolsForGroup(groupId: ChatLocalToolGroupId): LocalToolDefinition[] {
    return LOCAL_TOOLS.filter(tool => tool.groupId === groupId);
  }

  protected isToolGroupDisabled(groupId: ChatLocalToolGroupId): boolean {
    return this.getToolsForGroup(groupId).every(tool => !this.isLocalToolEnabled(tool.id));
  }

  protected getSelectedLocalToolLabel(): string {
    const tool = this.getLocalToolById(this.selectedLocalToolId);
    return tool ? `${this.getGroupLabel(tool.groupId)} / ${tool.label}` : '';
  }

  protected getToolGroupDescription(groupId: ChatLocalToolGroupId): string {
    return LOCAL_TOOL_GROUPS.find(group => group.id === groupId)?.description ?? '';
  }

  protected isLocalToolSelected(toolId: ChatLocalToolId): boolean {
    return this.selectedLocalToolId === toolId;
  }

  protected onSelectLocalTool(toolId: ChatLocalToolId): void {
    if (!this.isLocalToolEnabled(toolId)) {
      return;
    }
    this.selectedLocalToolId = toolId;
  }

  protected onClearSelectedLocalTool(): void {
    this.selectedLocalToolId = null;
  }

  protected isLocalToolEnabled(toolId: ChatLocalToolId): boolean {
    return this.getLocalToolDisabledReason(toolId) === null;
  }

  protected getLocalToolDisabledReason(toolId: ChatLocalToolId): string | null {
    switch (toolId) {
      case 'experiment.create_experiment':
        return null;
      case 'graph.create_graph':
      case 'graph.modify_graph':
        return this.hasGraphToolContext()
          ? null
          : 'Open from an experiment or replicate chat with graph context.';
      case 'experiment.duplicate_experiment':
      case 'experiment.auto_assign_devices':
      case 'data.export_experiment_data':
      case 'annotations.annotate_finding':
        return this.getCurrentToolTargetThingieId()
          ? null
          : 'Open from an experiment or replicate chat.';
    }
  }

  protected canSendWithSelectedTool(messageText: string): boolean {
    const selectedTool = this.getPendingSelectedTool();
    if (!selectedTool) {
      return true;
    }
    const tool = this.getLocalToolById(selectedTool.toolId);
    if (!tool?.requiresPrompt) {
      return true;
    }
    return messageText.length > 0;
  }

  private getPendingSelectedLocalToolId(): ChatLocalToolId | null {
    return this.selectedLocalToolId ?? this.pendingHiddenContext?.selectedTool?.toolId ?? null;
  }

  private getPendingSelectedTool(): ChatHiddenContext['selectedTool'] | undefined {
    const selectedToolId = this.getPendingSelectedLocalToolId();
    if (!selectedToolId) {
      return undefined;
    }
    const tool = this.getLocalToolById(selectedToolId);
    if (!tool) {
      return undefined;
    }
    return {
      groupId: tool.groupId,
      toolId: tool.id
    };
  }

  private getLocalToolById(toolId: ChatLocalToolId | null | undefined): LocalToolDefinition | undefined {
    if (!toolId) {
      return undefined;
    }
    return LOCAL_TOOLS.find(tool => tool.id === toolId);
  }

  private getGroupLabel(groupId: ChatLocalToolGroupId): string {
    return LOCAL_TOOL_GROUPS.find(group => group.id === groupId)?.label ?? groupId;
  }

  private hasGraphToolContext(): boolean {
    return !!this.getCurrentGraphToolThingieId();
  }

  private getCurrentGraphToolThingieId(): string | undefined {
    return this.pendingHiddenContext?.graphAgentContext?.thingieId
      ?? this.scopedConversationHiddenContext?.graphAgentContext?.thingieId;
  }

  private getCurrentToolTargetThingieId(hiddenContext?: ChatHiddenContext): string | undefined {
    const trimmedThingieId = hiddenContext?.graphAgentContext?.thingieId?.trim();
    if (trimmedThingieId) {
      return trimmedThingieId;
    }
    const linkedThingieId = this.currentConversationScope?.linkedThingieIds?.[0]?.trim();
    return linkedThingieId || undefined;
  }

  private withSelectedTool(
    hiddenContext: ChatHiddenContext | undefined,
    selectedTool?: ChatHiddenContext['selectedTool']
  ): ChatHiddenContext | undefined {
    if (!hiddenContext && !selectedTool) {
      return undefined;
    }
    if (!selectedTool) {
      return hiddenContext;
    }
    return {
      ...(hiddenContext ?? {}),
      selectedTool
    };
  }

  private clearComposerStateAfterSend(): void {
    this.messageInput.setValue('');
    this.selectedImages = [];
    this.pendingHiddenContext = undefined;
    this.pendingAttachmentIndicators = [];
    this.selectedLocalToolId = null;
  }

  private async dispatchSelectedToolMessage(
    message: string,
    hiddenContext: ChatHiddenContext | undefined,
    selectedTool?: ChatHiddenContext['selectedTool']
  ): Promise<SelectedToolDispatchResult> {
    if (!selectedTool) {
      return { handled: false, hiddenContext };
    }

    switch (selectedTool.toolId) {
      case 'experiment.create_experiment':
        return this.setupCopilotService.handleMessage(message, hiddenContext, {
          forceIntent: true,
          preferImmediateCreate: true
        });
      case 'experiment.duplicate_experiment': {
        const referenceThingieId = this.getCurrentToolTargetThingieId(hiddenContext);
        if (!referenceThingieId) {
          return {
            handled: true,
            hiddenContext,
            assistantMessage: 'Open this tool from an experiment or replicate chat so I know what to duplicate.'
          };
        }
        return {
          handled: true,
          hiddenContext,
          assistantMessage: 'I prepared the duplication workflow for the current experiment.',
          actionProposals: [{
            action: 'duplicateAndOpenWizard',
            explanation: 'Open the duplication wizard for the current experiment.',
            referenceThingieId
          }]
        };
      }
      case 'experiment.auto_assign_devices': {
        const thingieId = this.getCurrentToolTargetThingieId(hiddenContext);
        if (!thingieId) {
          return {
            handled: true,
            hiddenContext,
            assistantMessage: 'Open this tool from an experiment or replicate chat so I know where to assign devices.'
          };
        }
        return {
          handled: true,
          hiddenContext,
          assistantMessage: 'I prepared automatic device assignment for the current experiment.',
          actionProposals: [{
            action: 'autoAssignDevices',
            thingieId,
            explanation: message || 'Auto-assign devices for the current experiment.'
          }]
        };
      }
      case 'data.export_experiment_data': {
        const thingieId = this.getCurrentToolTargetThingieId(hiddenContext);
        if (!thingieId) {
          return {
            handled: true,
            hiddenContext,
            assistantMessage: 'Open this tool from an experiment or replicate chat so I know what data to export.'
          };
        }
        return {
          handled: true,
          hiddenContext,
          assistantMessage: 'I prepared an export action for the current experiment.',
          actionProposals: [{
            action: 'prepareExportBundle',
            thingieId,
            explanation: message || 'Export the current experiment data.'
          }]
        };
      }
      case 'annotations.annotate_finding': {
        const thingieId = this.getCurrentToolTargetThingieId(hiddenContext);
        if (!thingieId) {
          return {
            handled: true,
            hiddenContext,
            assistantMessage: 'Open this tool from an experiment or replicate chat so I know where to add the annotation.'
          };
        }
        if (!message.trim()) {
          return {
            handled: true,
            hiddenContext,
            assistantMessage: 'Tell me what finding you want to annotate, and I will prepare the annotation action.'
          };
        }
        return {
          handled: true,
          hiddenContext,
          assistantMessage: 'I prepared an annotation for this finding.',
          actionProposals: [{
            action: 'annotateFinding',
            thingieId,
            label: message.trim(),
            explanation: 'Create an annotation from the provided finding.'
          }]
        };
      }
      case 'graph.create_graph':
      case 'graph.modify_graph': {
        const tool = this.getLocalToolById(selectedTool.toolId);
        return {
          handled: false,
          hiddenContext,
          forwardedMessage: this.buildSelectedToolPrompt(tool, message)
        };
      }
    }
  }

  private buildSelectedToolPrompt(tool: LocalToolDefinition | undefined, message: string): string {
    if (!tool) {
      return message;
    }
    const toolHeading = `${this.getGroupLabel(tool.groupId)} / ${tool.label}`;
    return [
      `Selected DOTS local tool: ${toolHeading}.`,
      'Interpret the user request strictly within this tool workflow.',
      'When applicable, prefer DOTS local action proposals over generic prose.',
      `User request: ${message.trim()}`
    ].join('\n');
  }

  public ngOnDestroy(): void {
    this.stopThinkingMessages();
    this.stopSidebarResize();
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = null;
    }
    this.messagesContainer?.nativeElement.removeEventListener('scroll', this.handleMessagesScroll);
    this.subs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startThinkingMessages(): void {
    // Start with the first message
    this.thinkingMessageIndex = 0;
    this.thinkingMessage = this.thinkingMessages[this.thinkingMessageIndex];

    // Rotate messages every 4 seconds
    this.thinkingInterval = setInterval(() => {
      this.thinkingMessageIndex = (this.thinkingMessageIndex + 1) % this.thinkingMessages.length;
      this.thinkingMessage = this.thinkingMessages[this.thinkingMessageIndex];
    }, 4000);
  }

  private stopThinkingMessages(): void {
    if (this.thinkingInterval) {
      clearInterval(this.thinkingInterval);
      this.thinkingInterval = null;
    }
    this.thinkingMessage = '';
  }

  // Conversation management methods
  private async loadConversations(): Promise<void> {
    this.isLoadingConversations = true;
    try {
      this.conversations = await firstValueFrom(this.chatService.getConversations(this.currentConversationScope));
    } catch (error) {
      this.log.error('Failed to load conversations', error);
    } finally {
      this.isLoadingConversations = false;
    }
  }

  protected onNewConversation(): void {
    // Save current conversation before starting new one
    if (this.currentConversationId && this.messages.length > 0) {
      this.autoSaveConversation();
    }

    // Reset state for new conversation
    this.currentConversationId = null;
    this.messages = [];
    this.promptAuditTrail = [];
    this.currentChatkitPromptAudit = null;
    this.formattedMessageCache = new WeakMap<ChatMessage, string>();
    this.error = null;
    this.lastErrorIndex = null;
    this.messageInput.setValue('');
    this.selectedImages = [];
    this.pendingHiddenContext = undefined;
    this.scopedConversationHiddenContext = undefined;
    this.pendingAttachmentIndicators = [];
    this.selectedLocalToolId = null;
    this.streamingReasoning = '';
    this.streamingContent = '';
    this.streamingActionProposals = [];
    this.applyingActionProposalKeys.clear();
    this.isStreaming = false;
    this.isLoading = false;
    this.currentStatus = '';
    this.shouldAutoScroll = true;
    this.expandedReasoningIndices.clear();
    this.expandedMessageIndices.clear();
    this.conversationRating = 0;
    this.feedbackInput.setValue('');
    this.ratingDismissed = false;
    this.showRatingPrompt = false;
    this.providerLocked = false;
    this.forceDirectMode = false;

    // Auto-close the overlay panel
    this.showSidebar = false;

    void this.loadDefaultModel();
  }

  protected onSelectConversation(conversation: IChatConversation): void {
    // Save current conversation before switching
    if (this.currentConversationId && this.messages.length > 0) {
      this.autoSaveConversation();
    }

    this.currentConversationScope = this.getScopeFromConversation(conversation);

    // Load selected conversation
    this.currentConversationId = conversation.id;
    this.messages = [...conversation.messages];
    this.promptAuditTrail = [...(conversation.promptAuditTrail ?? [])];
    this.formattedMessageCache = new WeakMap<ChatMessage, string>();
    this.error = null;
    this.lastErrorIndex = null;
    this.expandedReasoningIndices.clear();
    this.expandedMessageIndices.clear();
    this.streamingReasoning = '';
    this.streamingContent = '';
    this.streamingActionProposals = [];
    this.applyingActionProposalKeys.clear();
    this.isStreaming = false;
    this.isLoading = false;
    this.currentStatus = '';
    this.shouldAutoScroll = true;

    // Restore provider/model state
    this.selectedProvider = conversation.provider || 'openai';
    this.selectedModel = conversation.modelUsed || '';
    this.providerLocked = conversation.messages.length > 0;
    this.selectedLocalToolId = null;

    // Restore rating state
    this.conversationRating = conversation.rating || 0;
    this.feedbackInput.setValue(conversation.feedback || '');
    this.ratingDismissed = false;
    this.updateRatingPromptVisibility();

    // Auto-close the overlay panel
    this.showSidebar = false;

    // Scroll to bottom after loading
    setTimeout(() => this.scrollToBottom(), 0);
  }

  protected onDeleteConversation(conversation: IChatConversation, event: Event): void {
    event.stopPropagation();

    this.subs.add(this.chatService.deleteConversation(conversation.id).subscribe({
      next: () => {
        // Remove from local list
        this.conversations = this.conversations.filter(c => c.id !== conversation.id);

        // If deleted conversation was active, clear the chat
        if (this.currentConversationId === conversation.id) {
          this.onNewConversation();
        }

        this.log.info('Conversation deleted');
      },
      error: (error: unknown) => {
        this.log.error('Failed to delete conversation', error);
        this.error = 'Failed to delete conversation';
      }
    }));
  }

  protected onStartRenameConversation(conversation: IChatConversation, event: Event): void {
    event.stopPropagation();
    this.editingConversationId = conversation.id;
    this.editingTitle = conversation.title;
  }

  protected onSaveRename(conversation: IChatConversation): void {
    if (this.editingTitle.trim()) {
      const updatedConversation: IChatConversation = {
        ...conversation,
        title: this.editingTitle.trim(),
        updatedAt: new Date().toISOString()
      };

      this.subs.add(this.chatService.saveConversation(updatedConversation).subscribe({
        next: () => {
          // Update local list
          const index = this.conversations.findIndex(c => c.id === conversation.id);
          if (index !== -1) {
            this.conversations[index] = updatedConversation;
          }
          this.editingConversationId = null;
          this.editingTitle = '';
        },
        error: (error: unknown) => {
          this.log.error('Failed to rename conversation', error);
        }
      }));
    }
  }

  protected onCancelRename(): void {
    this.editingConversationId = null;
    this.editingTitle = '';
  }

  protected onRenameKeydown(event: KeyboardEvent, conversation: IChatConversation): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onSaveRename(conversation);
    } else if (event.key === 'Escape') {
      this.onCancelRename();
    }
  }

  protected toggleSidebar(): void {
    this.showSidebar = !this.showSidebar;
  }

  private updateRatingPromptVisibility(): void {
    const hasAssistantMessage = this.messages.some(m => m.role === 'assistant');
    const currentConv = this.conversations.find(c => c.id === this.currentConversationId);
    const alreadyRated = !!currentConv?.rating;
    this.showRatingPrompt = hasAssistantMessage && !alreadyRated && !this.ratingDismissed;
  }

  private closeProviderSelectors(): void {
    this.providerSelect?.close();
    this.modelSelect?.close();
  }

  private autoSaveConversation(): void {
    if (this.messages.length === 0) {
      return;
    }

    const now = new Date().toISOString();

    if (this.currentConversationId) {
      // Update existing conversation
      const existingConversation = this.conversations.find(c => c.id === this.currentConversationId);
      if (existingConversation) {
        const updatedConversation: IChatConversation = {
          ...existingConversation,
          messages: [...this.messages],
          promptAuditTrail: [...this.promptAuditTrail],
          updatedAt: now
        };

        this.subs.add(this.chatService.saveConversation(updatedConversation).subscribe({
          next: (saved) => {
            // Update local list
            const index = this.conversations.findIndex(c => c.id === saved.id);
            if (index !== -1) {
              this.conversations[index] = saved;
            }
            // Re-sort by updatedAt
            this.conversations.sort(
              (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
            );
          },
          error: (error: unknown) => {
            this.log.error('Failed to auto-save conversation', error);
          }
        }));
      }
    } else {
      // Create new conversation
      const firstUserMessage = this.messages.find(m => m.role === 'user');
      const title = firstUserMessage
        ? generateConversationTitle(this.getMessageText(firstUserMessage))
        : 'New conversation';

      const newConversation: IChatConversation = {
        id: new ObjectID().toHexString(),
        title,
        messages: [...this.messages],
        promptAuditTrail: [...this.promptAuditTrail],
        provider: this.selectedProvider,
        modelUsed: this.selectedModel || undefined,
        source: 'direct',
        createdAt: now,
        updatedAt: now
      };

      if (this.currentConversationScope) {
        newConversation.scopeKind = this.currentConversationScope.scopeKind;
        newConversation.linkedThingieIds = [...(this.currentConversationScope.linkedThingieIds ?? [])];
        newConversation.experimentGroupId = this.currentConversationScope.experimentGroupId;
        newConversation.contextLabel = this.currentConversationScope.contextLabel;
        newConversation.sharingMode = this.currentConversationScope.sharingMode;
      }

      this.subs.add(this.chatService.saveConversation(newConversation).subscribe({
        next: (saved) => {
          this.currentConversationId = saved.id;
          // Add to local list at the beginning
          this.conversations.unshift(saved);
        },
        error: (error: unknown) => {
          this.log.error('Failed to save new conversation', error);
        }
      }));
    }
  }

  private async openScopedConversation(scope: IChatConversationScope, hiddenContext?: ChatHiddenContext): Promise<void> {
    await this.startNewConversation(scope, { hiddenContext });
  }

  private async openConversationById(conversationId: string): Promise<void> {
    const conversation = await firstValueFrom(this.chatService.getConversation(conversationId));
    if (!conversation) {
      return;
    }

    this.onNewConversation();
    this.currentConversationScope = this.getScopeFromConversation(conversation);
    this.scopedConversationHiddenContext = this.extractScopedConversationHiddenContext(conversation.messages);
    await this.loadConversations();
    const matchingConversation = this.conversations.find(c => c.id === conversation.id) ?? conversation;
    this.onSelectConversation(matchingConversation);
  }

  private getScopeFromConversation(conversation: IChatConversation): IChatConversationScope | null {
    if (!this.isScopedConversation(conversation)) {
      return null;
    }

    return {
      scopeKind: conversation.scopeKind ?? 'replicate',
      linkedThingieIds: [...(conversation.linkedThingieIds ?? [])],
      experimentGroupId: conversation.experimentGroupId,
      contextLabel: conversation.contextLabel,
      sharingMode: 'replicate_access'
    };
  }

  private async startNewConversation(
    scope: IChatConversationScope | null,
    options?: {
      imageDataUrl?: string;
      hiddenContext?: ChatHiddenContext;
      attachmentIndicators?: ChatAttachmentIndicator[];
    }
  ): Promise<void> {
    this.onNewConversation();
    this.currentConversationScope = scope === null
      ? null
      : {
        ...scope,
        linkedThingieIds: [...(scope.linkedThingieIds ?? [])]
      };

    if (options?.imageDataUrl) {
      this.selectedImages.push(options.imageDataUrl);
    }
    this.pendingHiddenContext = this.cloneHiddenContext(options?.hiddenContext);
    this.selectedLocalToolId = options?.hiddenContext?.selectedTool?.toolId ?? null;
    this.scopedConversationHiddenContext = this.extractScopedHiddenContext(options?.hiddenContext);
    if (options?.attachmentIndicators?.length) {
      this.pendingAttachmentIndicators = [...options.attachmentIndicators];
    }
    this.forceDirectMode = this.hasChartAnalysis(this.pendingHiddenContext);

    if (this.currentConversationScope) {
      await this.loadConversations();
    }
  }

  protected conversationHasImages(conversation: IChatConversation): boolean {
    return conversation.messages.some(msg => {
      if (typeof msg.content === 'string') {
        return false;
      }
      return msg.content.some(item => item.type === 'image_url');
    });
  }

  private cloneHiddenContext(
    hiddenContext?: ChatHiddenContext,
    options?: { stripExperimentSamplePayloads?: boolean }
  ): ChatHiddenContext | undefined {
    if (!hiddenContext) {
      return undefined;
    }

    const chartDataCsv = hiddenContext.chartDataCsv?.trim();
    const chartAnalysisCsv = hiddenContext.chartAnalysis?.csvContent?.trim();
    const chartAnalysisFilename = hiddenContext.chartAnalysis?.csvFilename?.trim();
    const graphAgentContext = hiddenContext.graphAgentContext
      ? structuredClone(hiddenContext.graphAgentContext)
      : undefined;
    const setupCopilotContext = hiddenContext.setupCopilotContext
      ? structuredClone(hiddenContext.setupCopilotContext)
      : undefined;
    const selectedTool = hiddenContext.selectedTool
      ? structuredClone(hiddenContext.selectedTool)
      : undefined;
    if (!chartDataCsv
      && !(chartAnalysisCsv && chartAnalysisFilename && hiddenContext.chartAnalysis)
      && !graphAgentContext
      && !setupCopilotContext
      && !selectedTool) {
      return undefined;
    }

    const clonedHiddenContext: ChatHiddenContext = {};
    if (chartDataCsv) {
      clonedHiddenContext.chartDataCsv = chartDataCsv;
    }
    if (chartAnalysisCsv && chartAnalysisFilename && hiddenContext.chartAnalysis) {
      clonedHiddenContext.chartAnalysis = {
        csvFilename: chartAnalysisFilename,
        csvContent: chartAnalysisCsv,
        requestedToolMode: hiddenContext.chartAnalysis.requestedToolMode
      };
    }
    if (graphAgentContext) {
      clonedHiddenContext.graphAgentContext = graphAgentContext;
      if (options?.stripExperimentSamplePayloads && clonedHiddenContext.graphAgentContext.experimentSampleData) {
        clonedHiddenContext.graphAgentContext.experimentSampleData = {
          manifest: structuredClone(clonedHiddenContext.graphAgentContext.experimentSampleData.manifest)
        };
      }
    }
    if (setupCopilotContext) {
      clonedHiddenContext.setupCopilotContext = setupCopilotContext;
    }
    if (selectedTool) {
      clonedHiddenContext.selectedTool = selectedTool;
    }

    return clonedHiddenContext;
  }

  private extractScopedHiddenContext(hiddenContext?: ChatHiddenContext): ChatHiddenContext | undefined {
    const graphAgentContext = hiddenContext?.graphAgentContext
      ? structuredClone(hiddenContext.graphAgentContext)
      : undefined;
    return graphAgentContext ? { graphAgentContext } : undefined;
  }

  private extractScopedConversationHiddenContext(messages: ChatMessage[]): ChatHiddenContext | undefined {
    for (let index = messages.length - 1; index >= 0; index--) {
      const hiddenContext = this.extractScopedHiddenContext(messages[index].hiddenContext);
      if (hiddenContext?.graphAgentContext) {
        return hiddenContext;
      }
    }
    return undefined;
  }

  private extractSetupConversationHiddenContext(messages: ChatMessage[]): ChatHiddenContext | undefined {
    for (let index = messages.length - 1; index >= 0; index--) {
      const hiddenContext = this.cloneHiddenContext(messages[index].hiddenContext);
      const setupCopilotContext = hiddenContext?.setupCopilotContext;
      if (setupCopilotContext) {
        return {
          setupCopilotContext,
          ...(this.shouldResumeSelectedSetupTool(hiddenContext) ? { selectedTool: hiddenContext.selectedTool } : {})
        };
      }
    }
    return undefined;
  }

  private getConversationLevelHiddenContext(): ChatHiddenContext | undefined {
    const graphAgentContext = this.scopedConversationHiddenContext?.graphAgentContext
      ? structuredClone(this.scopedConversationHiddenContext.graphAgentContext)
      : undefined;
    const setupCopilotHiddenContext = this.extractSetupConversationHiddenContext(this.messages);
    const setupCopilotContext = setupCopilotHiddenContext?.setupCopilotContext
      ? structuredClone(setupCopilotHiddenContext.setupCopilotContext)
      : undefined;
    const selectedTool = setupCopilotHiddenContext?.selectedTool
      ? structuredClone(setupCopilotHiddenContext.selectedTool)
      : undefined;
    if (!graphAgentContext && !setupCopilotContext && !selectedTool) {
      return undefined;
    }
    return {
      ...(graphAgentContext ? { graphAgentContext } : {}),
      ...(setupCopilotContext ? { setupCopilotContext } : {}),
      ...(selectedTool ? { selectedTool } : {})
    };
  }

  private shouldResumeSelectedSetupTool(hiddenContext?: ChatHiddenContext): boolean {
    const setupCopilotContext = hiddenContext?.setupCopilotContext;
    return !!hiddenContext?.selectedTool
      && !!setupCopilotContext
      && (setupCopilotContext.pendingQuestion !== undefined || setupCopilotContext.taskConfigurationSession !== undefined);
  }

  private mergeHiddenContexts(
    scopedHiddenContext?: ChatHiddenContext,
    pendingHiddenContext?: ChatHiddenContext
  ): ChatHiddenContext | undefined {
    const graphAgentContext = pendingHiddenContext?.graphAgentContext ?? scopedHiddenContext?.graphAgentContext;
    const setupCopilotContext = pendingHiddenContext?.setupCopilotContext ?? scopedHiddenContext?.setupCopilotContext;
    const chartDataCsv = pendingHiddenContext?.chartDataCsv?.trim();
    const chartAnalysis = pendingHiddenContext?.chartAnalysis;
    const selectedTool = pendingHiddenContext?.selectedTool ?? scopedHiddenContext?.selectedTool;

    if (!graphAgentContext && !setupCopilotContext && !chartDataCsv && !chartAnalysis && !selectedTool) {
      return undefined;
    }

    const mergedHiddenContext: ChatHiddenContext = {};
    if (graphAgentContext) {
      mergedHiddenContext.graphAgentContext = structuredClone(graphAgentContext);
    }
    if (setupCopilotContext) {
      mergedHiddenContext.setupCopilotContext = structuredClone(setupCopilotContext);
    }
    if (chartDataCsv) {
      mergedHiddenContext.chartDataCsv = chartDataCsv;
    }
    if (chartAnalysis) {
      mergedHiddenContext.chartAnalysis = {
        csvFilename: chartAnalysis.csvFilename,
        csvContent: chartAnalysis.csvContent,
        requestedToolMode: chartAnalysis.requestedToolMode
      };
    }
    if (selectedTool) {
      mergedHiddenContext.selectedTool = structuredClone(selectedTool);
    }

    return mergedHiddenContext;
  }

  protected getMessageActionProposals(message: ChatMessage): ActionProposal[] {
    return [...(message.actionProposals ?? [])];
  }

  protected hasSetupTaskConfiguration(message: ChatMessage): boolean {
    const taskConfigurationSession = this.getSetupTaskConfigurationSession(message);
    if (!taskConfigurationSession?.focusedTask) {
      return false;
    }
    return !message.actionProposals?.some(proposal => proposal.action === 'createExperimentFromChat');
  }

  protected getSetupTaskConfigurationProgressLabel(message: ChatMessage): string {
    const taskConfigurationSession = this.getSetupTaskConfigurationSession(message);
    if (!taskConfigurationSession?.focusedTask) {
      return '';
    }

    const focusedBlockingIndex = this.getFocusedBlockingTaskIndex(taskConfigurationSession);
    if (focusedBlockingIndex >= 0) {
      return `Blocking task ${focusedBlockingIndex + 1} of ${taskConfigurationSession.blockingTasks.length}`;
    }
    if (taskConfigurationSession.blockingTasks.length === 0) {
      return 'Task configuration complete';
    }
    return `${taskConfigurationSession.blockingTasks.length} blocking tasks remaining`;
  }

  protected getSetupTaskConfigurationDescription(message: ChatMessage): string {
    const focusedTask = this.getFocusedSetupTask(message);
    if (!focusedTask) {
      return '';
    }

    if (focusedTask.missingInputLabels.length > 0) {
      return `Required inputs: ${focusedTask.missingInputLabels.join(', ')}`;
    }
    return 'Please complete the required task inputs below.';
  }

  protected getSetupTaskConfigurationTask(message: ChatMessage): ITask | undefined {
    const focusedTask = this.getFocusedSetupTask(message);
    const taskConfigurationSession = this.getSetupTaskConfigurationSession(message);
    if (!focusedTask || !taskConfigurationSession) {
      return undefined;
    }

    return taskConfigurationSession.templateThingie.processes[focusedTask.processIdx]?.tasks[focusedTask.taskIdx];
  }

  protected getSetupTaskConfigurationThingie(message: ChatMessage): IThingie | undefined {
    return this.getSetupTaskConfigurationSession(message)?.templateThingie;
  }

  protected canFocusPreviousSetupTask(message: ChatMessage): boolean {
    const taskConfigurationSession = this.getSetupTaskConfigurationSession(message);
    return this.getFocusedBlockingTaskIndex(taskConfigurationSession) > 0;
  }

  protected canFocusNextSetupTask(message: ChatMessage): boolean {
    const taskConfigurationSession = this.getSetupTaskConfigurationSession(message);
    const focusedBlockingIndex = this.getFocusedBlockingTaskIndex(taskConfigurationSession);
    return focusedBlockingIndex >= 0 && focusedBlockingIndex < (taskConfigurationSession?.blockingTasks.length ?? 0) - 1;
  }

  protected canContinueSetupTaskConfiguration(message: ChatMessage): boolean {
    const taskConfigurationSession = this.getSetupTaskConfigurationSession(message);
    if (!taskConfigurationSession?.focusedTask) {
      return false;
    }

    return this.getFocusedBlockingTaskIndex(taskConfigurationSession) === -1;
  }

  protected onSetupTaskConfigurationTaskChange(messageIndex: number): void {
    const message = this.messages[messageIndex];
    const hiddenContext = this.cloneHiddenContext(message?.hiddenContext);
    const setupCopilotContext = hiddenContext?.setupCopilotContext;
    if (!message || !hiddenContext || !setupCopilotContext) {
      return;
    }

    const nextSetupCopilotContext = this.setupCopilotService.syncTaskConfigurationContext(setupCopilotContext);
    if (nextSetupCopilotContext.taskConfigurationSession?.blockingTasks.length === 0) {
      message.content = 'Task configuration looks complete. Continue to review the experiment draft.';
    }
    this.updateMessageSetupCopilotContext(messageIndex, nextSetupCopilotContext, hiddenContext, message);
  }

  protected onFocusPreviousSetupTask(messageIndex: number): void {
    this.shiftSetupTaskFocus(messageIndex, -1);
  }

  protected onFocusNextSetupTask(messageIndex: number): void {
    this.shiftSetupTaskFocus(messageIndex, 1);
  }

  protected onContinueSetupTaskConfiguration(messageIndex: number): void {
    const message = this.messages[messageIndex];
    const hiddenContext = this.cloneHiddenContext(message?.hiddenContext);
    const setupCopilotContext = hiddenContext?.setupCopilotContext;
    if (!message || !hiddenContext || !setupCopilotContext) {
      return;
    }

    const result = this.setupCopilotService.continueTaskConfiguration(setupCopilotContext, hiddenContext);
    const nextHiddenContext = this.cloneHiddenContext(result.hiddenContext);
    this.messages[messageIndex] = {
      ...message,
      content: result.assistantMessage ?? message.content,
      hiddenContext: nextHiddenContext,
      actionProposals: result.actionProposals
    };
    this.pendingHiddenContext = nextHiddenContext;
    this.saveSubject.next();
  }

  protected getActionProposalTitle(proposal: ActionProposal): string {
    switch (proposal.action) {
      case 'createGraphTab':
        return proposal.label ? `Create graph "${proposal.label}"` : 'Create graph';
      case 'duplicateAndModifyGraphTab':
        return proposal.label ? `Duplicate as "${proposal.label}"` : 'Duplicate graph';
      case 'addSeriesToGraph':
        return 'Add series to graph';
      case 'removeSeriesFromGraph':
        return 'Remove series from graph';
      case 'replaceGraphTabSeries':
        return 'Replace graph series';
      case 'renameGraphTab':
        return proposal.newLabel ? `Rename graph to "${proposal.newLabel}"` : 'Rename graph';
      case 'selectGraphTab':
        return 'Switch to graph tab';
      case 'duplicateAndOpenWizard':
        return 'Open duplication wizard';
      case 'createExperimentFromChat':
        return `Create experiment "${proposal.draft.experimentName}"`;
      case 'openWizardAtStep':
        return 'Open setup in wizard';
      case 'openRelevantView':
        return 'Open view';
      case 'openWizardForTaskConfiguration':
        return 'Open task configuration';
      case 'prepareExportBundle':
        return 'Export experiment data';
      case 'autoAssignDevices':
        return 'Auto-assign devices';
      case 'annotateFinding':
        return 'Create annotation';
      case 'applyProjectToSetupPrefill':
        return 'Apply project';
      case 'createOrAdjustWizardConfig':
        return 'Update experiment setup';
      case 'resolveTaskTemplateSlot':
        return 'Select task template';
    }
  }

  protected getActionProposalPreview(proposal: ActionProposal): string {
    switch (proposal.action) {
      case 'duplicateAndOpenWizard':
        return proposal.explanation;
      case 'createExperimentFromChat':
        return [
          `Template: ${proposal.draft.templateName}`,
          `Replicates: ${proposal.draft.replicateCount}`,
          `Project: ${proposal.draft.projectTitle}`
        ].join(' · ');
      case 'openWizardAtStep':
        return [
          proposal.prefill.templateName ? `Template: ${proposal.prefill.templateName}` : undefined,
          proposal.prefill.replicateCount ? `Replicates: ${proposal.prefill.replicateCount}` : undefined,
          proposal.prefill.projectTitle ? `Project: ${proposal.prefill.projectTitle}` : undefined
        ].filter(Boolean).join(' · ') || proposal.explanation;
      case 'renameGraphTab':
        return proposal.newLabel ? `New label: ${proposal.newLabel}` : proposal.explanation;
      case 'selectGraphTab':
        return proposal.explanation;
      case 'openRelevantView':
        return proposal.explanation;
      case 'openWizardForTaskConfiguration':
        return proposal.explanation;
      case 'prepareExportBundle':
        return proposal.explanation;
      case 'autoAssignDevices':
        return proposal.explanation;
      case 'annotateFinding':
        return proposal.label.length > 120
          ? proposal.label.substring(0, 117) + '...'
          : proposal.label;
      case 'applyProjectToSetupPrefill':
        return `Project: ${proposal.projectTitle}`;
      case 'createOrAdjustWizardConfig': {
        const parts: string[] = [];
        if (proposal.experimentName) {
          parts.push(`Name: ${proposal.experimentName}`);
        }
        if (proposal.replicateCount !== undefined) {
          parts.push(`Replicates: ${proposal.replicateCount}`);
        }
        if (proposal.organismName) {
          parts.push(`Organism: ${proposal.organismName}`);
        }
        if (proposal.templateName) {
          parts.push(`Template: ${proposal.templateName}`);
        }
        if (proposal.measurementGoals?.length) {
          parts.push(`Goals: ${proposal.measurementGoals.join(', ')}`);
        }
        return parts.join(' \u00b7 ') || proposal.explanation;
      }
      case 'resolveTaskTemplateSlot':
        return [
          proposal.selectedTaskTemplateName ? `Task template: ${proposal.selectedTaskTemplateName}` : undefined,
          proposal.templateName ? `in ${proposal.templateName}` : undefined
        ].filter(Boolean).join(' ') || proposal.explanation;
      default: {
        const pieces: string[] = [];
        if (proposal.label) {
          pieces.push(`Label: ${proposal.label}`);
        }
        if (proposal.seriesNames?.length) {
          pieces.push(`Series: ${proposal.seriesNames.join(', ')}`);
        } else if (proposal.seriesKeys?.length) {
          pieces.push(`Series: ${proposal.seriesKeys.length} selected`);
        }
        if (proposal.timeReference) {
          pieces.push(`Time: ${proposal.timeReference}`);
        }
        return pieces.join(' · ') || proposal.explanation;
      }
    }
  }

  protected isActionProposalApplying(messageIndex: number, proposalIndex: number): boolean {
    return this.applyingActionProposalKeys.has(this.getActionProposalKey(messageIndex, proposalIndex));
  }

  protected async onApplyActionProposal(messageIndex: number, proposalIndex: number): Promise<void> {
    const proposal = this.messages[messageIndex]?.actionProposals?.[proposalIndex];
    if (!proposal) {
      return;
    }

    const key = this.getActionProposalKey(messageIndex, proposalIndex);
    this.applyingActionProposalKeys.add(key);

    try {
      if (proposal.action === 'duplicateAndOpenWizard') {
        await this.applyDuplicateExperimentAction(proposal);
      } else if (proposal.action === 'createExperimentFromChat') {
        await this.applyCreateExperimentFromChatAction(proposal, messageIndex);
      } else if (proposal.action === 'openWizardAtStep') {
        await this.applyOpenWizardAtStepAction(proposal);
      } else if (proposal.action === 'openRelevantView') {
        await this.applyOpenRelevantViewAction(proposal);
      } else if (proposal.action === 'openWizardForTaskConfiguration') {
        await this.applyOpenWizardForTaskConfigurationAction(proposal);
      } else if (proposal.action === 'prepareExportBundle') {
        await this.applyPrepareExportBundleAction(proposal);
      } else if (proposal.action === 'autoAssignDevices') {
        await this.applyAutoAssignDevicesAction(proposal, messageIndex);
      } else if (proposal.action === 'annotateFinding') {
        await this.applyAnnotateFindingAction(proposal);
      } else if (proposal.action === 'applyProjectToSetupPrefill') {
        await this.applyApplyProjectToSetupPrefillAction(proposal, messageIndex);
      } else if (proposal.action === 'createOrAdjustWizardConfig') {
        await this.applyCreateOrAdjustWizardConfigAction(proposal, messageIndex);
      } else if (proposal.action === 'resolveTaskTemplateSlot') {
        await this.applyResolveTaskTemplateSlotAction(proposal, messageIndex);
      } else {
        const result = await this.graphActionExecutor.applyProposal(
          proposal,
          this.getHiddenContextForActionProposal(messageIndex)
        );
        if (!result.ok) {
          this.uiNotifications.displaySnackbar({
            message: result.errors?.join(' ') ?? 'Failed to apply graph action.'
          });
          return;
        }

        this.uiNotifications.displaySnackbar({
          message: result.savedLabel
            ? `Saved graph "${result.savedLabel}".`
            : 'Saved graph action.'
        });
      }

      this.removeActionProposal(messageIndex, proposalIndex);
    } catch (error) {
      this.log.error('Failed to apply action proposal', error);
      this.uiNotifications.displaySnackbar({
        message: error instanceof Error ? error.message : 'Failed to apply action proposal.'
      });
    } finally {
      this.applyingActionProposalKeys.delete(key);
    }
  }

  protected onDismissActionProposal(messageIndex: number, proposalIndex: number): void {
    this.removeActionProposal(messageIndex, proposalIndex);
  }

  private async applyDuplicateExperimentAction(proposal: DuplicateExperimentWizardActionProposal): Promise<void> {
    if (!proposal.referenceThingieId) {
      throw new Error('Missing reference thingie for duplication.');
    }

    await this.router.navigate(['/thingies/create'], {
      queryParams: {
        mode: 'DUPLICATE',
        referenceId: proposal.referenceThingieId,
        duplicateReferenceOnly: proposal.duplicateReferenceOnly === true ? 'true' : 'false'
      }
    });

    this.uiNotifications.displaySnackbar({
      message: 'Opened duplication wizard.'
    });
  }

  private async applyCreateExperimentFromChatAction(
    proposal: CreateExperimentFromChatActionProposal,
    messageIndex?: number
  ): Promise<void> {
    const setupCopilotContext = messageIndex === undefined
      ? undefined
      : this.messages[messageIndex]?.hiddenContext?.setupCopilotContext;
    const result = await this.setupCopilotService.createExperimentFromDraft(proposal.draft, setupCopilotContext);
    if (!result.ok || !result.createdThingies || result.createdThingies.length === 0) {
      throw new Error(result.errors?.join(' ') ?? 'Failed to create experiment from chat.');
    }

    this.uiNotifications.displaySnackbar({
      message: `Experiment "${proposal.draft.experimentName}" created successfully.`
    });

    if (result.createdThingies.length === 1) {
      await this.router.navigate(['thingies', 'thingie', result.createdThingies[0]._id]);
      return;
    }

    await this.router.navigate(['/dashboard/cards'], {
      queryParams: { tab: 'planned' },
      state: { refreshDashboard: true }
    });
  }

  private async applyOpenWizardAtStepAction(
    proposal: OpenWizardAtStepActionProposal
  ): Promise<void> {
    const navigationState: IThingieCreationNavigationState = {
      setupCopilotPrefill: proposal.prefill
    };
    await this.router.navigate(['/thingies/create'], {
      queryParams: {
        mode: 'CREATE',
        initialStep: proposal.initialStep
      },
      state: navigationState
    });

    this.uiNotifications.displaySnackbar({
      message: 'Opened the setup wizard with the current Copilot recommendation.'
    });
  }

  private async applyOpenRelevantViewAction(
    proposal: OpenRelevantViewActionProposal
  ): Promise<void> {
    switch (proposal.viewType) {
      case 'thingie_detail':
        if (!proposal.thingieId) {
          throw new Error('Missing thingie ID for navigation.');
        }
        await this.router.navigate(['thingies', 'thingie', proposal.thingieId]);
        break;
      case 'graph_configuration':
        if (!proposal.thingieId) {
          throw new Error('Missing thingie ID for navigation.');
        }
        await this.router.navigate(['thingies', 'thingie', proposal.thingieId, 'graph-configuration']);
        break;
      case 'dashboard':
        await this.router.navigate(['/dashboard/cards']);
        break;
    }

    this.uiNotifications.displaySnackbar({
      message: 'Navigated to the requested view.'
    });
  }

  private async applyOpenWizardForTaskConfigurationAction(
    proposal: OpenWizardForTaskConfigurationActionProposal
  ): Promise<void> {
    if (!proposal.thingieId) {
      throw new Error('Missing thingie ID for task configuration.');
    }

    const thingie = await firstValueFrom(this.thingieService.getThingieById(proposal.thingieId, true));

    await this.router.navigate(['/thingies/create'], {
      queryParams: {
        mode: 'EDIT',
        initialStep: WizardStep.GeneralTaskConfiguration
      },
      state: { thingies: [thingie] } as any
    });

    this.uiNotifications.displaySnackbar({
      message: 'Opened wizard at task configuration.'
    });
  }

  private async applyPrepareExportBundleAction(
    proposal: PrepareExportBundleActionProposal
  ): Promise<void> {
    if (!proposal.thingieId) {
      throw new Error('Missing thingie ID for export.');
    }

    const thingie = await firstValueFrom(this.thingieService.getThingieById(proposal.thingieId, true));
    const relations = await firstValueFrom(this.thingieService.getRelationsOfThingieById(proposal.thingieId));

    const experiments: ExperimentSelection[] = [
      {
        experimentName: thingie.baseName,
        replicateSelections: [
          { thingie, graphSelections: [] },
          ...relations.map(rel => ({ thingie: rel, graphSelections: [] }))
        ]
      }
    ];

    await this.thingieImportExportService.showExportDialog({
      thingieId: thingie._id,
      levelMode: 'replicate',
      canSwitchLevelMode: relations.length > 0,
      title: `Export\u00a0of\u00a0"${thingie.name}"\u00a0from\u00a0"${thingie.baseName}"`,
      experiments
    });
  }

  private async applyAutoAssignDevicesAction(
    proposal: AutoAssignDevicesActionProposal,
    messageIndex: number
  ): Promise<void> {
    const hiddenContext = this.getHiddenContextForActionProposal(messageIndex);
    const thingieId = proposal.thingieId || hiddenContext?.graphAgentContext?.thingieId;
    if (!thingieId) {
      throw new Error('Missing thingie ID for device auto-assignment.');
    }

    const thingie = await firstValueFrom(this.thingieService.getThingieById(thingieId, true));
    const relations = await firstValueFrom(this.thingieService.getRelationsOfThingieById(thingieId));
    const allThingies = [thingie, ...relations];

    const devices = await firstValueFrom(this.deviceService.getDevices());

    const { DeviceAutoAssignService } = await import('src/app/services/agent/device-auto-assign.service');
    const autoAssignService = new DeviceAutoAssignService();
    const result = autoAssignService.autoAssign(devices, allThingies);

    if (!result.ok) {
      this.uiNotifications.displaySnackbar({
        message: result.errors?.join(' ') ?? 'Failed to auto-assign devices.'
      });
      return;
    }

    for (const t of allThingies) {
      if (t._id) {
        await this.thingieService.updateThingie(t);
      }
    }

    this.uiNotifications.displaySnackbar({
      message: result.assignedCount > 0
        ? `Auto-assigned ${result.assignedCount} device(s).`
        : 'No additional devices could be assigned.'
    });
  }

  private async applyAnnotateFindingAction(
    proposal: AnnotateFindingActionProposal
  ): Promise<void> {
    if (!proposal.thingieId) {
      throw new Error('Missing thingie ID for annotation.');
    }
    if (!proposal.label?.trim()) {
      throw new Error('Missing annotation text.');
    }

    const user = await this.userService.getCurrentUser();
    await this.thingieService.createAnnotations({
      thingies: [proposal.thingieId],
      annotations: [{
        label: proposal.label,
        timestamp: new Date().toISOString(),
        creator: user?.displayName ?? 'Copilot',
        deleted: false,
        hidden: false,
        files: [],
        systemAnnotation: false
      }]
    });

    this.uiNotifications.displaySnackbar({
      message: 'Annotation created successfully.'
    });
  }

  private async applyApplyProjectToSetupPrefillAction(
    proposal: ApplyProjectToSetupPrefillActionProposal,
    messageIndex: number
  ): Promise<void> {
    const message = this.messages[messageIndex];
    const hiddenContext = this.cloneHiddenContext(message?.hiddenContext);
    if (!message || !hiddenContext) {
      throw new Error('Cannot apply project: no hidden context available.');
    }

    const setupCopilotContext: SetupCopilotContext = {
      ...(hiddenContext.setupCopilotContext ?? {}),
      projectId: proposal.projectId,
      projectTitle: proposal.projectTitle
    };

    if (setupCopilotContext.pendingQuestion === 'project') {
      setupCopilotContext.pendingQuestion = undefined;
    }

    this.updateMessageSetupCopilotContext(messageIndex, setupCopilotContext, hiddenContext, message);

    this.uiNotifications.displaySnackbar({
      message: `Project set to "${proposal.projectTitle}".`
    });
  }

  private async applyCreateOrAdjustWizardConfigAction(
    proposal: CreateOrAdjustWizardConfigActionProposal,
    messageIndex: number
  ): Promise<void> {
    const message = this.messages[messageIndex];
    const hiddenContext = this.cloneHiddenContext(message?.hiddenContext);
    if (!message || !hiddenContext) {
      throw new Error('Cannot update setup config: no hidden context available.');
    }

    const setupCopilotContext: SetupCopilotContext = {
      ...(hiddenContext.setupCopilotContext ?? {})
    };

    if (proposal.experimentName !== undefined) {
      setupCopilotContext.experimentName = proposal.experimentName;
    }
    if (proposal.replicateCount !== undefined) {
      setupCopilotContext.replicateCount = proposal.replicateCount;
    }
    if (proposal.organismName !== undefined) {
      setupCopilotContext.organismName = proposal.organismName;
    }
    if (proposal.organismClass !== undefined) {
      setupCopilotContext.organismClass = proposal.organismClass;
    }
    if (proposal.measurementGoals !== undefined) {
      setupCopilotContext.measurementGoals = proposal.measurementGoals;
    }

    this.updateMessageSetupCopilotContext(messageIndex, setupCopilotContext, hiddenContext, message);

    this.uiNotifications.displaySnackbar({
      message: 'Experiment setup updated.'
    });
  }

  private async applyResolveTaskTemplateSlotAction(
    proposal: ResolveTaskTemplateSlotActionProposal,
    messageIndex: number
  ): Promise<void> {
    const message = this.messages[messageIndex];
    const hiddenContext = this.cloneHiddenContext(message?.hiddenContext);
    if (!message || !hiddenContext) {
      throw new Error('Cannot resolve task template slot: no hidden context available.');
    }

    const setupCopilotContext: SetupCopilotContext = {
      ...(hiddenContext.setupCopilotContext ?? {})
    };

    const recommendations = setupCopilotContext.recommendations;
    if (recommendations) {
      const recommendation = recommendations.find(r => r.templateId === proposal.templateId);
      if (recommendation?.taskSlots) {
        const slot = recommendation.taskSlots.find(
          s => s.processIdx === proposal.processIdx && s.taskIdx === proposal.taskIdx
        );
        if (slot) {
          slot.options = [{
            taskTemplateId: proposal.selectedTaskTemplateId,
            taskTemplateName: proposal.selectedTaskTemplateName ?? proposal.selectedTaskTemplateId
          }];
        }
      }
    }

    this.updateMessageSetupCopilotContext(messageIndex, setupCopilotContext, hiddenContext, message);

    this.uiNotifications.displaySnackbar({
      message: `Task template selected: ${proposal.selectedTaskTemplateName ?? proposal.selectedTaskTemplateId}`
    });
  }

  private removeActionProposal(messageIndex: number, proposalIndex: number): void {
    const message = this.messages[messageIndex];
    if (!message?.actionProposals) {
      return;
    }

    const nextProposals = message.actionProposals.filter((_, index) => index !== proposalIndex);
    this.messages[messageIndex] = {
      ...message,
      actionProposals: nextProposals.length > 0 ? nextProposals : undefined
    };
    this.saveSubject.next();
  }

  private getSetupTaskConfigurationSession(message: ChatMessage): SetupCopilotTaskConfigurationSession | undefined {
    return message.hiddenContext?.setupCopilotContext?.taskConfigurationSession;
  }

  protected getFocusedSetupTask(message: ChatMessage): SetupCopilotBlockingTask | undefined {
    return this.getSetupTaskConfigurationSession(message)?.focusedTask;
  }

  private getFocusedBlockingTaskIndex(
    taskConfigurationSession?: SetupCopilotTaskConfigurationSession
  ): number {
    if (!taskConfigurationSession?.focusedTask) {
      return -1;
    }

    return taskConfigurationSession.blockingTasks.findIndex(
      task => task.processIdx === taskConfigurationSession.focusedTask?.processIdx
        && task.taskIdx === taskConfigurationSession.focusedTask?.taskIdx
    );
  }

  private shiftSetupTaskFocus(messageIndex: number, delta: number): void {
    const message = this.messages[messageIndex];
    const hiddenContext = this.cloneHiddenContext(message?.hiddenContext);
    const taskConfigurationSession = hiddenContext?.setupCopilotContext?.taskConfigurationSession;
    const setupCopilotContext = hiddenContext?.setupCopilotContext;
    if (!message || !hiddenContext || !taskConfigurationSession || !setupCopilotContext) {
      return;
    }

    const focusedBlockingIndex = this.getFocusedBlockingTaskIndex(taskConfigurationSession);
    if (focusedBlockingIndex < 0) {
      return;
    }

    const nextFocusedTask = taskConfigurationSession.blockingTasks[focusedBlockingIndex + delta];
    if (!nextFocusedTask) {
      return;
    }

    setupCopilotContext.taskConfigurationSession = {
      ...taskConfigurationSession,
      focusedTask: structuredClone(nextFocusedTask)
    };
    this.updateMessageSetupCopilotContext(messageIndex, setupCopilotContext, hiddenContext, message);
  }

  private updateMessageSetupCopilotContext(
    messageIndex: number,
    setupCopilotContext: SetupCopilotContext,
    hiddenContext: ChatHiddenContext,
    currentMessage: ChatMessage
  ): void {
    const nextHiddenContext: ChatHiddenContext = {
      ...hiddenContext,
      setupCopilotContext
    };
    this.messages[messageIndex] = {
      ...currentMessage,
      hiddenContext: nextHiddenContext
    };
    this.pendingHiddenContext = nextHiddenContext;
    this.saveSubject.next();
  }

  private getHiddenContextForActionProposal(messageIndex: number): ChatHiddenContext | undefined {
    const messageHiddenContext = this.cloneHiddenContext(this.messages[messageIndex]?.hiddenContext);
    if (messageHiddenContext?.graphAgentContext) {
      return messageHiddenContext;
    }

    for (let index = messageIndex - 1; index >= 0; index--) {
      const message = this.messages[index];
      if (message.role !== 'user') {
        continue;
      }

      const hiddenContext = this.cloneHiddenContext(message.hiddenContext);
      if (hiddenContext?.graphAgentContext) {
        return hiddenContext;
      }
    }

    return undefined;
  }

  private shouldParseTextActionProposals(): boolean {
    return this.selectedProvider !== 'openai'
      && this.selectedProvider !== 'anthropic'
      && this.selectedProvider !== 'gemini';
  }

  private parseActionProposalsFromMessage(message: ChatMessage): ActionProposal[] {
    const match = this.getMessageText(message).match(/```dots-actions\s*([\s\S]*?)```/i);
    if (!match?.[1]) {
      return [];
    }

    try {
      const parsed = JSON.parse(match[1].trim()) as { actions?: ActionProposal[] } | ActionProposal[];
      const actions = Array.isArray(parsed) ? parsed : parsed.actions;
      return Array.isArray(actions) ? actions : [];
    } catch (error) {
      this.log.warn('Failed to parse dots-actions block', error);
      return [];
    }
  }

  private mergeActionProposals(existing: ActionProposal[], incoming: ActionProposal[]): ActionProposal[] {
    const merged = [...existing];
    const seen = new Set(existing.map(proposal => JSON.stringify(proposal)));
    for (const proposal of incoming) {
      const key = JSON.stringify(proposal);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      merged.push(proposal);
    }
    return merged;
  }

  private getActionProposalKey(messageIndex: number, proposalIndex: number): string {
    return `${messageIndex}:${proposalIndex}`;
  }

  private hasChartAnalysis(hiddenContext?: ChatHiddenContext): boolean {
    return !!hiddenContext?.chartAnalysis?.csvContent?.trim()
      && !!hiddenContext.chartAnalysis.csvFilename?.trim();
  }

  private shouldUseProviderPythonAnalysis(hiddenContext?: ChatHiddenContext): boolean {
    if (!this.hasChartAnalysis(hiddenContext)) {
      return false;
    }

    switch (this.selectedProvider) {
      case 'openai':
        return true;
      case 'anthropic':
        return this.selectedModel.trim().startsWith('claude');
      default:
        return false;
    }
  }

  protected formatConversationDate(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString(undefined, { weekday: 'long' });
    } else {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }

  // Image lightbox methods
  protected onImageClick(imageUrl: string): void {
    this.lightboxImage = imageUrl;
  }

  protected onCloseLightbox(): void {
    this.lightboxImage = null;
  }

  @HostListener('document:keydown.escape')
  protected onEscapeKey(): void {
    if (this.isResizingSidebar) {
      this.stopSidebarResize();
    } else if (this.lightboxImage) {
      this.onCloseLightbox();
    } else {
      this.onClose();
    }
  }

  @HostListener('document:pointermove', ['$event'])
  protected onSidebarResizeMove(event: PointerEvent): void {
    if (!this.isResizingSidebar || this.activeResizePointerId !== event.pointerId) {
      return;
    }

    const nextWidth = window.innerWidth - event.clientX;
    this.sidebarService.setSidebarWidth(nextWidth);
  }

  @HostListener('document:pointerup', ['$event'])
  protected onSidebarResizeEnd(event: PointerEvent): void {
    if (!this.isResizingSidebar || this.activeResizePointerId !== event.pointerId) {
      return;
    }

    this.stopSidebarResize();
  }

  @HostListener('document:pointercancel', ['$event'])
  protected onSidebarResizeCancel(event: PointerEvent): void {
    if (!this.isResizingSidebar || this.activeResizePointerId !== event.pointerId) {
      return;
    }

    this.stopSidebarResize();
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
        this.showScrollToBottom = false;
      }
    }, 0);
  }

  private scrollReasoningContentToBottom(): void {
    setTimeout(() => {
      if (this.streamingReasoningContent) {
        const element = this.streamingReasoningContent.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 0);
  }

  private scrollToLastMessageStart(): void {
    setTimeout(() => {
      if (this.messagesContainer) {
        const container = this.messagesContainer.nativeElement;
        // Find the last message element
        const messages = container.querySelectorAll('.message');
        const lastMessage = messages[messages.length - 1] as HTMLElement;
        if (lastMessage) {
          // Scroll so the top of the last message is visible near the top of the container
          const messageTop = lastMessage.offsetTop;
          container.scrollTop = messageTop - 10; // Small offset for visual padding
        }
      }
    }, 0);
  }

  private readonly handleMessagesScroll = (): void => {
    const element = this.messagesContainer?.nativeElement;
    if (!element) {
      return;
    }

    this.updateScrollState(element);
  };

  private updateScrollState(element: HTMLDivElement): void {
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;
    this.shouldAutoScroll = isNearBottom;
    this.showScrollToBottom = !isNearBottom && this.messages.length > 0;
  }

  private scrollToBottomIfNeeded(): void {
    if (!this.shouldAutoScroll) {
      return;
    }

    this.scrollToBottom();
  }

  private scrollReasoningContentToBottomIfNeeded(): void {
    if (!this.shouldAutoScroll) {
      return;
    }

    this.scrollReasoningContentToBottom();
  }

  private stopSidebarResize(): void {
    if (!this.isResizingSidebar) {
      return;
    }

    this.isResizingSidebar = false;
    this.activeResizePointerId = null;
    document.body.classList.remove('copilot-sidebar-resizing');
  }

  private hasPromptAuditEntry(candidate: IPromptAuditEntry): boolean {
    return this.promptAuditTrail.some(entry => entry.provider === candidate.provider
      && entry.deliveryMode === candidate.deliveryMode
      && entry.resolutionSource === candidate.resolutionSource
      && entry.appliedPrompt === candidate.appliedPrompt
      && (entry.label || '') === (candidate.label || '')
      && (entry.versionTag || '') === (candidate.versionTag || '')
      && (entry.stateVariableName || '') === (candidate.stateVariableName || ''));
  }
}
